/**
 * @file src/multi/createCollectionApp.js
 * @description Boots the multi-stage Orbiter Studio loaded from a collection (decisions/0004).
 * Ties the React stage layout (`MultiStageStudio`) to the existing multi-orbiter realm
 * (`createMultiOrbiterApp` + `ViewportCompositor`, fed the stages as its cells) so a whole collection
 * composes in ONE DOM / ONE AudioContext with NO host bridge:
 *
 *   - roster + permissions + saved layout come from the collection-data client (stubbed today);
 *   - the React layout owns the stage geometry — the compositor reads each stage's rect, so resize is free;
 *   - focus is `voiceRegistry` (the stage ↔ voice mapping lives here; the layout stays voice-agnostic);
 *   - changing the visible stage count reconciles the realm's voices to that count (direct add/remove,
 *     no reload, no audio cut for surviving voices);
 *   - edit affordances are gated on `canEdit`; persist is a one-shot authed write.
 *
 * The realm-level audio unlock is the app-wide one armed in `installEnvGuards` (first user gesture) — one
 * unlock for the whole realm, never per-voice.
 */
import { createMultiOrbiterApp } from './createMultiOrbiterApp.js';
import { createViewportCompositor } from './renderHost.js';
import { clampSubtype, defaultSubtype } from './stageGeometry.js';
import {
  buildDesiredArrangement,
  cruiseResumeIndex,
  moveEntryBefore,
  planReconcile,
  planRestoredArrangement,
  swapDescriptor,
} from './stageArrangement.js';
import { voiceRegistry } from '../voice/VoiceRegistry.js';
import { MIDIControllerInstance } from '../input/midi/MIDIController.js';
import { saveCollectionLayout, removeCollectionItem } from '../api/collectionDataService.js';
import { readCollectionArrangement, saveCollectionArrangement, forgetLocalCollectionArrangement } from '../api/collectionArrangementService.js';
import { readMidiScopePreference, saveMidiScopePreference } from '../input/midi/midiSliceSource.js';
import { primeTrackReleaseCache, primeOrbiterReleaseCache, primeWorldReleaseCache, fetchTrackRelease, fetchOrbiterRelease, fetchEntangledWorldRelease, ensurePlayableTrackRelease } from '../api/dataManager/loaders.js';
import { mountMultiStageStudio } from '../ui/react/studio/MultiStageStudio.tsx';
import { entityMeta } from '../ui/react/studio/entityMeta.ts';
import { hideLoadingScreen } from '../boot/loadingScreen.js';
import { LOAD_PROGRESS_EVENT, LOAD_ERROR_EVENT } from '../boot/loadProgress.js';
import { bootTrace, bootTraceStep } from '../boot/bootTrace.js';
import notifications from '../core/AppNotifications.js';
import { getT } from '../i18n/index.js';
import { isAutofocusEnabled } from '../input/midi/midiControlSettings.js';
import { KeyboardController } from '../input/KeyboardController.js';
import { getRoomIdFromUrl, getRoomDecksFromUrl, getRoomPlayersFromUrl } from '../utils/urlParams.js';
import { setRoomRoster, setRoomVoiceOrder, clearRoomRoster } from '../room/roomRoster.js';
import { ensureRoomClientArmed } from '../sync/init.js';
import { updateRoomVoiceOrder, roomOwnsDeck, getRoomClient } from '../room/roomClient.js';
import { roomStore } from '../room/roomStore.js';
import { roomScore } from '../room/roomScore.js';
import { HAND_KINDS, drawHand, drawKind, resolveDrawConfig } from '../room/handDraw.js';
import { INLINE_SCOPE } from '../local/localAudioConfig.js';
import { isLocalEntryId } from '../local/localAudioIds.js';
import { createLocalRailController } from '../local/localRailController.js';
import { publishDeckLoad, publishDeckClear, notifyDeckLoaded, markDeckLoadFailed, setDeckLoadApplier, setDeckClearApplier, setDeckContentsProvider, setDeckPendingProvider, setHandConsumedHandlers, resetDeckBridge } from '../room/roomDeckBridge.js';

// Every voice in the realm plays over a TRACK audio spine, and the TRACK is the only entity that
// carries related entities — a track has a world and an orbiter. Orbiters and worlds each stand on
// their own and carry no audio: a world release is visuals only, and an orbiter is an instrument,
// not a recording. The linked ids on an orbiter release name the session its author built and
// tested it against — an editing reference, never a playback input — so they are not read here.
// That makes "bootable" (can this card start a stage by itself) exactly "is it a track". Every
// other card is still fully draggable onto an OCCUPIED stage, where it swaps only its own dimension
// of that live session (see `swapStage`).
function isBootableEntry(entry) {
  return Boolean(entry?.trackId);
}

// The entity a roster entry STANDS FOR — its kind (spelled as the roster spells it) and that kind's
// id. A track entry can also carry its related orbiter/world ids, so the track is checked first: the
// card is a track card, and the related ids only describe the session its author built.
function entryKind(entry) {
  // The entry's OWN type wins. The id fields are a fallback for an entry that never carried one,
  // and they are not a safe primary: `mapRosterEntry` fills `trackId` from `entityId` for an
  // untyped entry, and hydration can add related ids later — so an orbiter card that ever acquires
  // a `trackId` would be filed under `track:<id>`, and the hand's orbiter would never resolve. It
  // would then render "Unavailable" on the clients whose hydration had run and not on the others,
  // which is a divergence rather than a shared placeholder.
  const declared = drawKind(entry?.entityType);
  if (declared) return declared;
  if (entry?.trackId) return 'track';
  if (entry?.orbiterId) return 'orbiter';
  if (entry?.entangledWorldId) return 'entangled-world';
  return null;
}
function entryEntityId(entry) {
  // The kind→id-field mapping is entityMeta's (its `idKey`), not restated here: a second copy would
  // silently keep a stale mapping the day a kind's field changes, and every card of that kind would
  // deal as Unavailable.
  const idKey = entityMeta(entryKind(entry)).idKey;
  return (idKey && entry?.[idKey]) || null;
}

/**
 * A hand card whose entity the pool cannot resolve — it was archived, or its fetch failed. It still
 * takes its place on the rail, as an UNAVAILABLE card. Dropping it silently would leave one player
 * looking at five cards and the other at six with no way to tell which one went missing, and the
 * whole point of the draw is that both players see the same hand.
 */
function unavailableHandCard(kind, id) {
  return {
    voiceId: `unavailable::${kind}::${id}`,
    entityType: kind,
    [entityMeta(kind).idKey]: id,
    title: null,
    bootable: false,
    unavailable: true,
  };
}

// Bounded background release warm-up (see `prefetchReleases`). At `hydrationLevel=1` the collection
// fetch carries cards only, so a release is fetched lazily on drag-to-stage. To keep an early drag from
// paying mycelium's slow per-item hydration, warm the cache for the first few drawer entries after the
// studio is up — capped in count and concurrency so it can never stampede the server or crowd out a
// real drag's fetch.
// How long the arrangement settles before it is written. Arranging comes in bursts (a drop, a
// clear, two reorders) and only the last state is worth a round trip. Matches the queue's own order
// write, for the same reason.
const PERSIST_DEBOUNCE_MS = 1500;
const PREFETCH_LIMIT = 6;
const PREFETCH_CONCURRENCY = 2;

// Shell MIDI targets (slot focus/add, pager, drawer), persisted per collection. Enabled by
// default; the env var is the build-time kill switch (Vite inlines it, so flipping it means a
// rebuild — same trade every VITE_ flag makes).
const COLLECTION_SHELL_MIDI_ENABLED = import.meta.env?.VITE_DISABLE_COLLECTION_SHELL_MIDI !== 'true';

// Removing a card from the collection (the rail's context menu). Enabled by default, same build-time
// kill-switch shape as the shell-MIDI flag above — it is the one action here that writes to the user's
// collection, so it can be turned off without a revert if the write ever misbehaves.
const COLLECTION_ITEM_REMOVE_ENABLED = import.meta.env?.VITE_DISABLE_COLLECTION_ITEM_REMOVE !== 'true';

// Collection-owned MIDI for the stage AUDIO controls: the mapping belongs to the collection + the
// stage POSITION, so the physical controls keep working when that stage's entity is replaced. Same
// kill-switch shape as the shell flag above; the in-studio Collection/Orbiter toggle is the choice.
const COLLECTION_MIDI_SCOPE_ENABLED = import.meta.env?.VITE_DISABLE_COLLECTION_MIDI_SCOPE !== 'true';

// A person's OWN arrangement of a collection, kept on their account (see the three records under
// "An arrangement has THREE RECORDS" below). Same build-time kill-switch shape as the flags above.
// Turning it off falls back to exactly what shipped before it: everyone reads the collection's own
// arrangement, and only editors can change what they come back to.
const PERSONAL_ARRANGEMENT_ENABLED = import.meta.env?.VITE_DISABLE_PERSONAL_ARRANGEMENT !== 'true';

// How long the boot waits for this person's own arrangement before opening on the collection's.
// The studio cannot build its stages until this is answered (it decides what is ON them), so the
// wait is real and has to be short: past it, the collection's arrangement is the honest fallback.
const PERSONAL_ARRANGEMENT_READ_TIMEOUT_MS = 4000;

/** Render a plain full-screen message (no-access / not-found / empty) instead of a broken realm. */
export function renderMessage(text) {
  hideLoadingScreen(); // the boot loader (z-9999) would otherwise cover the message
  const el = document.createElement('div');
  el.id = 'collection-message';
  el.style.cssText =
    'position:fixed;inset:0;z-index:60;display:flex;align-items:center;justify-content:center;padding:24px;text-align:center;color:rgba(255,255,255,0.8);background:#000;font-size:1rem;';
  el.textContent = text;
  if (typeof document !== 'undefined') document.body.appendChild(el);
  return { start: () => {}, dispose: () => el.remove() };
}

/**
 * @param {object} opts
 * @param {{collectionId:(string|null), roster:Array, permissions:{canView:boolean,canEdit:boolean}, layout:(object|null)}} opts.collectionData
 * @param {Function} opts.makeVoiceSession per-voice factory (Main supplies `makeOrbiterVoiceSession`).
 * @returns {{ start: () => Promise<void>, dispose: () => void }}
 */
export function createCollectionApp({ collectionData, makeVoiceSession }) {
  const {
    roster: rawRoster = [],
    permissions = { canView: true, canEdit: false },
    layout: savedLayout = null,
    // Display-only pool identity, handed to the waiting room so a gathering room shows the
    // collection you are about to play rather than an opaque room id.
    name: collectionName = null,
    coverImage: collectionCoverImage = null,
    // The room's sayable code, present only when this collection was read WITH a room grant. It
    // arrives with the grant rather than on the boot URL, so the waiting room can only ever show
    // the code of the room it is actually in.
    roomJoinCode = null,
    // Queue mode: tracks play once and auto-advance to the next entry
    // instead of looping — sequencing lives HERE in the multi-orbiter layer.
    sequential = false,
  } = collectionData || {};

  // The hydrationLevel=2 collection payload already contains each item's hydrated release
  // response — seed the release caches up front so a voice booted from ANY entry (drag-to-stage →
  // addVoice) gets its release as a warm cache hit instead of re-fetching the identical
  // multi-second payload. The drawer/stage entries stay lean (no fat payload retained).
  let primedCount = 0;
  const seededRoster = (Array.isArray(rawRoster) ? rawRoster : []).map((entry) => {
    if (entry?.hydrated) {
      if (entry.trackId && primeTrackReleaseCache(entry.trackId, entry.hydrated)) primedCount += 1;
      else if (entry.orbiterId && primeOrbiterReleaseCache(entry.orbiterId, entry.hydrated)) primedCount += 1;
      else if (entry.entangledWorldId && primeWorldReleaseCache(entry.entangledWorldId, entry.hydrated)) primedCount += 1;
      const { hydrated, ...lean } = entry;
      return lean;
    }
    return entry;
  });
  if (primedCount > 0) {
    console.info(`[collection] primed ${primedCount}/${seededRoster.length} release payloads from the collection fetch — voices booted from these entries skip their own release fetches`);
  }

  // Every entity type shows in the drawer; `bootable` is the studio-facing mirror of
  // `isBootableEntry` — can this card seed an EMPTY stage. It follows from the entity type alone, so
  // it never changes after this. Every card is draggable regardless: a non-bootable card still swaps
  // its dimension into an occupied stage.
  const roster = seededRoster.map((entry) => ({ ...entry, bootable: isBootableEntry(entry) }));

  if (!permissions.canView) return renderMessage("You don't have access to this collection.");
  // An empty collection still opens: its owner may be about to fill it with files from this
  // machine, and the studio's stages are the drop target. A room or the queue has nothing to
  // offer on an empty roster and says so.
  if (roster.length === 0 && (getRoomIdFromUrl() || sequential)) {
    return renderMessage('This collection is empty.');
  }

  // Stage index → the voiceId occupying it (null for an empty stage). The realm keys voices by voiceId,
  // so this mapping is deterministic from the arrangement.
  const slotVoiceIds = [];
  // Stage index → the SOURCE (drawer/roster) voiceId of the occupant (null for an empty stage). A dragged
  // placement's realm voiceId is minted (`entry::N`), so it never equals a drawer card's id; the studio
  // needs this raw id to mark the active drawer card + know which stages are filled.
  const slotSourceIds = [];

  // The stage POSITION a voice sits at (1-based), published to MIDI so a collection-owned mapping
  // keys off the position rather than its current occupant. Installed here (not read out of this module by
  // the controller) so this app stays the single owner of the stage order, and cleared on dispose —
  // the controller is a module singleton that outlives any one collection.
  const slotResolver = (voiceId) => {
    const index = slotVoiceIds.indexOf(voiceId);
    return index === -1 ? null : index + 1;
  };
  MIDIControllerInstance?.setSlotResolver(slotResolver);

  // Save/load MIDI to the COLLECTION or to the ORBITER. The choice is stored WITH the mappings — in
  // this collection's own slice on the account, not in browser storage — because a preference that
  // selects between two sets of mappings belongs beside them and has to follow the user to the next
  // machine the way the mappings do.
  //
  // Defaults to the ORBITER, i.e. to exactly what this collection did before this feature existed.
  // Defaulting to the collection would silently repoint every stage control at an empty slice on
  // first load: a user who had already mapped their controller in here would find the whole surface
  // blank, with their bindings intact but unread, and the only way back is a toggle that is hidden
  // outside MIDI-learn mode. A new scope must not move where existing mappings are read from.
  let collectionMidiEnabled = false;
  // Set once the user presses the toggle, so the in-flight preference read below cannot overrule a
  // choice they have already made (and already saved) while it was still on the wire.
  let userChoseMidiScope = false;

  function publishCollectionMidiContext() {
    MIDIControllerInstance?.setCollectionMappingContext({
      collectionId: COLLECTION_MIDI_SCOPE_ENABLED ? collectionData.collectionId || null : null,
      enabled: collectionMidiEnabled,
    });
  }

  function toggleCollectionMidi() {
    userChoseMidiScope = true;
    collectionMidiEnabled = !collectionMidiEnabled;
    publishCollectionMidiContext();
    studio?.update?.({ midiScope: { enabled: collectionMidiEnabled, onToggle: toggleCollectionMidi } });
    // Fire-and-forget: the choice is already live locally, and a failed write must not undo the
    // user's press or block the studio. It reconciles on the next boot's read.
    if (COLLECTION_MIDI_SCOPE_ENABLED && collectionData.collectionId) {
      void saveMidiScopePreference(
        collectionData.collectionId,
        collectionMidiEnabled ? 'collection' : 'orbiter',
      ).catch(() => {});
    }
  }

  // Publish the DEFAULT synchronously, before any voice starts — a stage's controls register and
  // hydrate inside `start()` and must already know which slice they own their mappings in. The
  // saved choice is a network read, so it lands after; re-publishing then re-keys the live widgets
  // (`setCollectionMappingContext` re-applies), which is the same path the toggle uses.
  publishCollectionMidiContext();
  if (COLLECTION_MIDI_SCOPE_ENABLED && collectionData.collectionId) {
    void readMidiScopePreference(collectionData.collectionId).then((saved) => {
      if (disposed || saved === null || userChoseMidiScope) return;
      const enabled = saved === 'collection';
      if (enabled === collectionMidiEnabled) return;
      collectionMidiEnabled = enabled;
      publishCollectionMidiContext();
      studio?.update?.({ midiScope: { enabled: collectionMidiEnabled, onToggle: toggleCollectionMidi } });
    });
  }
  // The reconcile target: how many stages to show. The studio's count switcher is the source of the
  // number; this is its single record here, set ONLY when the switcher reports a change. Arrange actions
  // (load/clear/reorder) reconcile to this without changing it, so a count change queued but not yet run
  // is never undone by a subsequent drop.
  let targetSubtype = defaultSubtype(savedLayout, roster.length);
  // The arrangement: which entry occupies each stage. A default stage front-fills from the roster; an
  // EXPLICIT assignment (drag-to-stage) or clear is recorded here as a placement entry (with its own
  // minted `voiceId`, or `null` for "deliberately empty"), so a cleared stage stays empty on the next
  // reconcile instead of re-filling from the roster.
  const stageOverrides = new Map();
  // The drawer's entry list (its own order, reorderable). Starts as roster order; the arrange front-fill
  // still reads the roster, so reordering the drawer is display + persist order only (matches root).
  const drawerEntries = roster.slice();
  // Monotonic suffix so each drag-to-stage placement gets a DISTINCT voiceId over the entry's shared
  // audio ids — a second instance of the same entry can play on another stage (product decision) without
  // colliding on the realm's voiceId key.
  let placementCounter = 0;
  const mintVoiceId = (entry) => `${entry.voiceId}::${(placementCounter += 1)}`;

  // Queue mode: stages pre-fill from the queue order (you came here to LISTEN, not to
  // arrange) — unlike collection mode, where stages deliberately start empty and the
  // user drags cards. Seeded as ordinary placements so every later interaction
  // (drag, clear, reorder) behaves exactly as in collection mode.
  if (sequential) {
    const seedable = roster.filter(isBootableEntry);
    for (let i = 0; i < targetSubtype && i < seedable.length; i++) {
      const entry = seedable[i];
      stageOverrides.set(i, { ...entry, voiceId: mintVoiceId(entry), sourceVoiceId: entry.voiceId });
    }
  }
  // A room boot is a multiplayer DECK session: the collection is the shared card POOL, and the room is
  // built from a FIXED number of decks (turntables) chosen at creation (`?decks=`, default 2) — never
  // from the pool size. Decks start EMPTY; each player deals a card onto the deck they own, and that
  // load is synced across every client. The deck count fixes the stage count here.
  //
  // WHERE that number comes from, in order of authority:
  //   1. the relay, once we are joined — it tells everyone the same pair, so it is the only source
  //      that can make two people agree. Adopted below, via the room store.
  //   2. the GRANT, at boot — recorded server-side at mint and handed to us on the collection read.
  //      This is what the creator's tab mints from, and what everyone renders until (1) arrives.
  //   3. the URL, and only when the grant carries no shape (older rooms). It is a request for a
  //      shape, never a statement of one: anyone can edit a link.
  const roomId = getRoomIdFromUrl();
  if (roomId) {
    const grantDecks = collectionData?.roomShape?.decks;
    targetSubtype = clampSubtype(grantDecks ?? getRoomDecksFromUrl());
  }

  // ── An arrangement has THREE RECORDS ─────────────────────────────────────────────────────────
  //
  // One arrangement — which cards are on which stages, and in what order the rail holds them — is
  // stored in three places, split by WHOSE it is rather than by which store was handy:
  //
  //   the COLLECTION's   its saved layout, a server-side object its EDITORS write and everyone who
  //                      can view it reads. The collection saying what it IS.
  //   the PERSON's       the same shape, filed per person on their account. Anyone who can VIEW the
  //                      collection may write it, which is the whole point: without it, someone who
  //                      cannot edit a collection has nowhere to keep the way they left it, while
  //                      an editor's identical gesture is remembered.
  //   this MACHINE's     `local:` ids, which name files on ONE machine and are meaningless anywhere
  //                      else, so they can never live in either record above. Kept in this
  //                      browser's local library, per collection.
  //
  // Three rules join them. Change one record and check the others: a stage is a single position,
  // whichever record names it.
  //
  //   1. ONE WRITER PER FACT. You write your arrangement wherever you have the RIGHT to. An
  //      editor's IS the collection's, so it goes to the collection layout — and clears their
  //      personal record, which would otherwise shadow the collection they have just described.
  //      Everyone else's goes to their personal record. Nobody writes the same fact twice, so
  //      there is nothing to keep in step and no staleness to guard against.
  //   2. ONE RESTORER. The grid is resolved ONCE, in `resolveArrangement()` at the top of
  //      `start()`: the person's record if they have one, otherwise the collection's — wholesale,
  //      never merged. Merging two sets of POSITIONS puts a card back on a stage its owner moved it
  //      off. The machine's local files then fill only the stages that grid left EMPTY, which is
  //      safe precisely because a `local:` id can never appear in the other two records — it can
  //      answer "which of my files", never "which stage".
  //   3. RESTORING NEVER WRITES. A restore that persists destroys real data: a save built from a
  //      half-restored arrangement sanitizes to nothing server-side and the backend stores `null`.
  //      This is why `loadStage` takes `{persist: false}`.
  //
  // WHICH RECORDS A MODE USES. The differences between the four modes are declared here, once,
  // rather than as branches scattered through the boot — adding a record is then a column in this
  // table and not a new `if`:
  //
  //   mode         grid + rail come from        writes to
  //   ----------   --------------------------   ------------------------------------------------
  //   collection   mine ?? the collection's     the collection's (editor) or mine (viewer), and
  //                                             this machine's local files in either case
  //   inline       nothing; stages start empty  local files only
  //   queue        front-fill from the queue    the queue order, + local files
  //   room         nothing; cards are DEALT     NOTHING
  //
  // The room row is empty and load-bearing. Its deck count comes from the grant or the relay (just
  // above), its decks start empty on purpose, and each player deals their own card with the load
  // synced to everyone else. Seeding stages would put cards on the table nobody dealt, on one
  // client only; writing an arrangement would save a table nobody set as somebody's own. A scored
  // room is further out of reach still: its rail is a HAND, a per-section subset each player then
  // reorders for themselves. A QUEUE is already an order the user owns, persisted to their account,
  // and its stages pre-fill from it because you came there to listen rather than to arrange.
  const arrangementMode = roomId ? 'room' : sequential ? 'queue' : collectionData.collectionId ? 'collection' : 'inline';
  // Only a real collection has records to read or write. An inline session has no id to file one
  // under, and the two modes above own their stages outright.
  const usesStoredArrangement = arrangementMode === 'collection';
  // Whether the collection's OWN layout is ours to write. An editor's arrangement is the
  // collection's; everyone else keeps their own. `inRoom()` as well as the boot's `roomId`, because
  // a room whose relay is lost for good becomes a solo session again — see `maybePersist`.
  const collectionLayoutIsOurs = () =>
    usesStoredArrangement && !roomId && !inRoom() && Boolean(permissions.canEdit);
  // This person's own arrangement, read once at the top of `start()` and null until then. Only ever
  // set for a mode that uses one, and only when they had one stored.
  let personalLayout = null;
  // The arrangement this session actually opens on — whichever record won in `resolveArrangement` —
  // and, from then on, this side's live copy of the studio's geometry (see `onGeometryChange` on the
  // mount). The studio reads its splits and focused stage from this rather than from `savedLayout`,
  // and writes them back to it, so the stages and the geometry around them can never come from two
  // different arrangements.
  let effectiveLayout = savedLayout;

  /**
   * Apply the arrangement this session opens on: the grid from rule 2 above, resolved once.
   *
   * Both records hold the same seven fields and are built by the same function (`currentLayout()`),
   * so one is substituted for the other WHOLESALE rather than merged — including the stages it
   * says are empty. A merge would put a card back on a stage its owner moved it off, which is the
   * single most confusing thing this could do.
   */
  function resolveArrangement() {
    if (!usesStoredArrangement) return;
    // Mine if I have one, otherwise the collection's.
    const layout = personalLayout || savedLayout;
    if (!layout) return;
    effectiveLayout = layout;

    const restored = planRestoredArrangement(layout, roster, isBootableEntry);
    if (restored.entries !== roster) {
      drawerEntries.length = 0;
      drawerEntries.push(...restored.entries);
    }
    // A FLOOR under the stage count, for a layout that carries no `subtype` of its own — one written
    // before the count was persisted, where the last occupied position is the only record of how
    // wide the studio was. Without it such an arrangement comes back silently missing its last
    // stages. For anything this version wrote it is a no-op, since `defaultSubtype` has already
    // taken the stored `subtype` above; keep BOTH, because `restored.stages` counts to the last
    // OCCUPIED stage and so cannot describe trailing empty ones on its own.
    // It only ever GROWS the count — a saved arrangement narrower than the collection's own subtype
    // leaves the extra stages showing as the empty placeholders they always were.
    if (restored.stages) targetSubtype = clampSubtype(Math.max(targetSubtype, restored.stages));
    // A personal arrangement states its own stage count too: it is part of how the person left the
    // studio, and a viewer who opened a third stage should find three.
    if (personalLayout?.subtype) targetSubtype = clampSubtype(Math.max(targetSubtype, personalLayout.subtype));
    restored.placements.forEach(({ index, entry }) => {
      if (index >= targetSubtype) return;
      // Minted exactly as a drag-to-stage placement is, so a restored stage is indistinguishable
      // from one the user just dropped and every later interaction behaves identically.
      stageOverrides.set(index, { ...entry, voiceId: mintVoiceId(entry), sourceVoiceId: entry.voiceId });
    });
    // Seed the live swap record from what was stored, for the stages that actually came back.
    //
    // Not cosmetic: `planRestoredArrangement` has already overlaid these onto the placements, so the
    // stages BOOT correctly either way — but the next persist rebuilds `swaps` from this map, and an
    // empty one would quietly erase every remembered pairing the first time anything else on the
    // rail moved. A stage whose card no longer resolved has no placement, and its swap goes with it.
    (Array.isArray(layout.swaps) ? layout.swaps : []).forEach((swap) => {
      if (!swap || !Number.isInteger(swap.index) || !stageOverrides.get(swap.index)) return;
      const rec = {};
      if (swap.orbiterId) {
        rec.orbiterId = swap.orbiterId;
        rec.orbiterVersion = null;
      }
      if (swap.entangledWorldId) {
        rec.entangledWorldId = swap.entangledWorldId;
        rec.entangledWorldVersion = null;
      }
      if (rec.orbiterId || rec.entangledWorldId) stageSwaps.set(swap.index, rec);
    });
  }

  // Whether a deal/eject routes through the ROOM is live state, not a URL fact: the room client tears
  // itself down when the relay connection is lost for good, and from that moment on every structural
  // change must take the solo path instead of routing into a dead room and being silently refused.
  const inRoom = () => Boolean(getRoomClient());

  // A SCORED room deals a HAND: instead of the whole collection, each section puts a few cards on the
  // rail — the same few for everyone in the room — and the next section deals again. The hand itself
  // never crosses the wire; it is computed from the room id, the score's section ids, the section
  // pointer and the dice, which every participant has identically (see `handDraw`). An unscored room,
  // and any session that is not a room at all, keeps the whole album exactly as before.
  //
  // What DOES cross the wire is the pair of inputs a link-joiner could not otherwise know: the draw
  // config and the pinned pool. The creator states both at `room:create` (from the grant, below) and
  // the relay replays them in every `room:joined` — so the room's answer, once joined, is the store's.
  //
  // The pool is the GRANT's pinned entities, not the card rail (each player reorders that for
  // themselves). The roster stands in only for the CREATOR, and only until a grant carries its own
  // pinned list: the creator's answer is then what the whole room deals from, so the two players
  // cannot diverge even while one list is roster-derived.
  const rosterPinned = roster
    .map((entry) => ({ id: entryEntityId(entry), kind: entryKind(entry) }))
    .filter((entity) => entity.id && entity.kind);
  const mintPinned = collectionData?.roomDraw?.pinned?.length ? collectionData.roomDraw.pinned : rosterPinned;
  // What `room:create` states as the draw CONFIG: enabled + composition only. The pool travels as
  // the top-level `pinned` beside it — embedding it here too would put the same list on the wire
  // twice and leave a second, relay-unvalidated copy sitting in the store.
  const wireDraw = () => {
    const raw = collectionData?.roomDraw;
    return raw ? { enabled: raw.enabled, composition: raw.composition } : null;
  };
  // `kind:id` → the roster entry standing for it. First wins, so a collection listing the same entity
  // twice still contributes one card to a hand.
  const entryByEntity = new Map();
  roster.forEach((entry) => {
    const key = `${entryKind(entry)}:${entryEntityId(entry)}`;
    if (!entryByEntity.has(key)) entryByEntity.set(key, entry);
  });
  // The live hand: the SOURCE voiceIds the rail may show, plus cards the hand named that the pool
  // could not resolve. Null = no hand at all (not a room, no score, or the draw switched off), which
  // is what puts the whole album back on the rail.
  let handSourceIds = null;
  let handMissingEntries = [];
  // The cards of THIS hand that have already been played, as `kind:id` entity keys — the same
  // identity the deal itself resolves through, and the only one that means the same thing on two
  // machines (a rail voiceId is a collection ITEM id, de-duplicated against each user's own order).
  // A played card leaves the rail and cannot be played again until the next deal refills the hand.
  let handConsumedKeys = new Set();
  // A peer's played set that arrived before this client had a hand to apply it to — held for the
  // very next deal and then dropped. Without it a client that hears the room's played cards a beat
  // early loses them for good, and then publishes an EMPTY set of its own over the relay's stored
  // copy, which is what a later joiner would repair from.
  let pendingHandConsumed = null;
  // What the current hand was dealt FROM — the frozen section, the dice, and the pool. A score tick
  // that changes none of them (a second passing) never re-deals or re-pushes. It doubles as the DEAL
  // TAG on the wire: everything that makes this hand this hand is already in it, so a snapshot
  // tagged with it describes the same cards on every client, and one from any other deal cannot.
  let handKey = null;
  let unsubscribeScore = null;

  // voiceId → unsubscribe, for the config-resolved listener below. A freshly added voice's
  // `dataManager.activeConfigRequest.orbiterId` (what `resolveFocusMidiPersistenceIds` reads) is not
  // populated yet when `addVoice` returns — the session's own config fetch is still in flight. Without
  // this, a slot's focus-MIDI target would register with a permanently-null persistence id whenever
  // nothing ELSE happens to push studio state again after that voice's config lands.
  const slotConfigUnsubscribes = new Map();

  let app = null;
  let studio = null;
  let studioHandle = null;
  let unsubscribeActive = null;
  let unsubscribeSelection = null;
  // Live subscription to the relay's account of the room shape; null outside room mode.
  let unsubscribeRoomShape = null;
  let disposed = false;
  // Serializes reconciles so rapid actions (count clicks, drops) can't interleave an `await addVoice`
  // with a shrink (the race that would orphan a voice). Each reconcile targets the CURRENT arrangement.
  let reconcileChain = Promise.resolve();
  // The coalesced layout write (see `maybePersist`): the timer, and the arrangement it will send.
  // FLUSHED on dispose rather than cancelled — the moment the user leaves the studio is precisely
  // when the arrangement they just made matters most, and dropping it would lose the last edit of
  // every session.
  let persistTimer = null;
  let persistPending = false;
  // Closing the tab, reloading, and the browser's OWN back button all leave without passing through
  // `handleBack` or `dispose`, and inside the debounce window that loses the arrangement outright —
  // which is most of how people actually leave. `pagehide` is the one event that fires for all of
  // them (including a bfcache suspend, where sending an unchanged layout again is harmless).
  const onPageHide = () => flushPersist(true);
  if (typeof window !== 'undefined') window.addEventListener('pagehide', onPageHide);

  // The real orbiter a live voice resolves to — the same identity the MIDI persistence layer keys
  // on and what "the same track" means when it's loaded onto more than one stage at once.
  function orbiterIdForVoice(voiceId) {
    return voiceRegistry.get(voiceId)?.dataManager?.activeConfigRequest?.orbiterId || null;
  }

  // `additive` toggles the stage in/out of the multi-selection instead of collapsing to
  // single focus. Two surfaces use it: the dock's orbiter map (shift-click a position, or drag across
  // several), and CameraController's shift-pointerdown on the tile itself — the latter calls
  // voiceRegistry.toggleSelection directly rather than through here, since it already holds the voice id.
  function focusStage(index, additive = false, fromMidi = false) {
    const voiceId = slotVoiceIds[index];
    if (!voiceId) return;
    try {
      if (additive) {
        voiceRegistry.toggleSelection(voiceId);
        return;
      }
      if (fromMidi) {
        // A track's MIDI mapping means "select THIS TRACK" — and the same track can be
        // dragged onto more than one stage at once (duplicate placements are allowed). A trigger
        // selects EVERY currently-loaded instance together (gang-focus) instead of an arbitrary
        // single one, which is what made the last-loaded instance silently win before this. The
        // TRIGGERED instance is always the primary (voiceRegistry.getActive()) — every single-focus
        // surface (keyboard, media keys, camera/sensor scoping) reads getActive(), and it must agree
        // with the tile the trigger actually fired from, not an arbitrary co-loaded sibling.
        const orbiterId = orbiterIdForVoice(voiceId);
        const coLoaded = orbiterId
          ? slotVoiceIds.filter((id) => id && id !== voiceId && orbiterIdForVoice(id) === orbiterId)
          : [];
        voiceRegistry.setActive(voiceId);
        coLoaded.forEach((id) => {
          try {
            voiceRegistry.addToSelection(id);
          } catch (_) {}
        });
        if (isAutofocusEnabled()) {
          studioHandle?.focusIndex?.(index);
        }
        return;
      }
      voiceRegistry.setActive(voiceId);
    } catch (_) {}
  }

  // A stable snapshot of the drawer list for the studio — re-sliced ONLY when the list actually changes
  // (reorder / remove), so a stage-only push doesn't hand the card rail a new array identity each time.
  let entriesSnapshot = drawerEntries.slice();
  // EVERY entry this session has known, by source voiceId — not a mirror of the current rail. A card
  // removed from the collection can still be PLAYING on a stage (removal edits the collection, it does
  // not stop the music), and the stage chrome, the room's deck snapshot and the hand lookup all resolve
  // their entry through here. So `removeEntry` drops the card from `drawerEntries` and leaves this map
  // alone; nothing that is still on screen loses its title and artwork.
  const entryBySourceVoiceId = new Map(drawerEntries.map((entry) => [entry.voiceId, entry]));

  // The local audio library: files on this machine, as cards at the end of the rail. Offered in a
  // collection or inline session only — a room deals a pool no other machine can see these in, and
  // the queue is a list of platform tracks the user keeps across devices, which a file here is not.
  // Behind its flag while phase 1 settles.
  const localRail =
    !roomId && !sequential
      ? createLocalRailController({
          // An inline (`?tracks=`) session is its own place too; its files are not the queue's or
          // any collection's.
          scopeKey: collectionData.collectionId || INLINE_SCOPE,
          // Remembered with the files, so the local files page can still name the collection they
          // came from after that collection has been deleted — which is the one case where those
          // files have nowhere else to be seen.
          scopeName: collectionData.name || null,
          applyEntries: applyLocalEntries,
          loadStage: (index, entry, options) => loadStage(index, entry, options),
          updateStudio: (props) => studio?.update?.(props),
          inRoom: () => inRoom(),
          readArrangement: () => ({
            railIds: drawerEntries.map((entry) => entry.voiceId),
            stageIds: stageSourceIds(),
            // One owner of "what is this stage playing", for hosted cards and local files alike —
            // the local record keeps only the entries whose stage it actually names.
            stageSwaps,
          }),
          // A stage the studio does not show, or one already holding a card, is not somewhere a
          // remembered local file may be put back: the collection's own saved layout is everyone's,
          // and one machine's file does not evict what it restored.
          stageIsFree: (index) => index >= 0 && index < targetSubtype && !stageOverrides.get(index),
        })
      : null;

  /** Replace the rail's LOCAL cards with `localEntries` (kept at the end, in their own order). The
   *  card that is playing keeps its entry in `entryBySourceVoiceId` even after removal, like a
   *  collection card does. */
  function applyLocalEntries(localEntries) {
    const hosted = drawerEntries.filter((entry) => !isLocalEntryId(entry.voiceId));
    drawerEntries.length = 0;
    drawerEntries.push(...hosted, ...localEntries);
    localEntries.forEach((entry) => entryBySourceVoiceId.set(entry.voiceId, entry));
    entriesSnapshot = drawerEntries.slice();
    pushStudioState();
    studio?.update?.({ noPlayableEntries: !drawerEntries.some(isBootableEntry) });
  }

  // The slot-focus MIDI mapping is orbiter-owned like every other mapping in this app
  // (the backend requires `entityId` to be a real, owned orbiter — there is no collection-scoped
  // identity to persist against). So each slot's binding lives with THAT slot's own real
  // occupant, not one anchor shared across all four buttons — swapping the track in slot A only
  // affects slot A's mapping, and never bleeds into slot B/C/D or another collection that happens
  // to share an occupant.
  function resolveFocusMidiPersistenceIds() {
    return slotVoiceIds.map((voiceId) => orbiterIdForVoice(voiceId));
  }

  /** Stage index → that stage's drawer/roster entry (title, image, …), looked up via the SOURCE
   *  voiceId — null for an empty stage. */
  function resolveStageEntries() {
    return slotSourceIds.map((sourceVoiceId) => (sourceVoiceId ? entryBySourceVoiceId.get(sourceVoiceId) ?? null : null));
  }

  // Cards with an in-flight drop/load (source voiceId → count of concurrent loads) — the studio
  // shows the orbit loader on those cards, so a drop that takes seconds visibly works instead of
  // looking ignored. Counted, not flagged: the same card can be loading onto two stages at once.
  const cardLoadCounts = new Map();
  function beginCardLoad(sourceVoiceId) {
    if (!sourceVoiceId) return;
    cardLoadCounts.set(sourceVoiceId, (cardLoadCounts.get(sourceVoiceId) ?? 0) + 1);
    pushStudioState();
  }
  function endCardLoad(sourceVoiceId) {
    if (!sourceVoiceId || !cardLoadCounts.has(sourceVoiceId)) return;
    const next = cardLoadCounts.get(sourceVoiceId) - 1;
    if (next > 0) cardLoadCounts.set(sourceVoiceId, next);
    else cardLoadCounts.delete(sourceVoiceId);
    if (!disposed) pushStudioState();
  }

  /** Push the live stage→source-voice map + drawer order back to the studio so its UI (active card, drop
   *  hints, filled stages) reflects the realm. A fresh stage array each call so React sees the change; the
   *  entries snapshot keeps its identity between reorders. */
  function pushStudioState() {
    studio?.update?.({
      stageVoiceIds: slotSourceIds.slice(),
      stageEntries: resolveStageEntries(),
      focusMidiPersistenceIds: resolveFocusMidiPersistenceIds(),
      entries: entriesSnapshot,
      loadingSourceIds: [...cardLoadCounts.keys()],
      handSourceIds,
      handMissingEntries,
      handConsumedIds: consumedSourceIds(),
    });
    // Publish the stage order so the mobile A/B/C/D picker labels each orbiter by its DESKTOP
    // slot (stage index), not registration order — otherwise slot B on the phone could drive orbiter C.
    voiceRegistry.setSlotOrder(slotVoiceIds);
    // Room mode: the stage→voice map IS the wire's positional voice map (index i = wire slot i). It
    // changes every time a deck loads or clears, so republish it — the room store re-seeds its
    // wire↔voice translation and attaches the deck's freshly-booted voice.
    if (roomId) {
      const order = slotVoiceIds.slice();
      setRoomVoiceOrder(order);
      updateRoomVoiceOrder(order);
    }
  }

  // Set the target count (when given) and queue a reconcile to it. Arrange actions call it with no
  // argument to reconcile the current arrangement at the existing target. Before boot it only records
  // the target; start() runs the first reconcile once the realm + stages exist.
  function enqueueReconcile(nextCount) {
    if (nextCount != null && clampSubtype(nextCount) !== targetSubtype) {
      targetSubtype = clampSubtype(nextCount);
      // Showing more or fewer stages IS part of the arrangement — someone who leaves four stages
      // open means to find four when they come back, whether or not every one of them holds a card.
      maybePersist();
    }
    if (!app || !studioHandle) return;
    reconcileChain = reconcileChain
      .then(() => reconcileTo())
      .catch((error) => console.warn('[collection] stage reconcile failed', error));
    return reconcileChain;
  }

  /**
   * Reconcile the realm's voices to the current arrangement over `targetSubtype` stages, idempotently,
   * against the CURRENTLY committed stage DOM. `planReconcile` computes the minimal remove/add set:
   * remove-pass first (so untouched stages keep their voice — no audio cut for survivors — and a replaced
   * stage frees before it re-adds), then add-pass for each stage whose desired entry has a committed cell.
   * Because it reconciles to the current committed arrangement (not a queued delta) and only adds when the
   * stage cell exists, interleaved actions converge without orphaning a voice into a removed stage.
   */
  // ── Auto-advance (cruise) ───────────────────────────────────────────────────
  // Per live voice: a listener for the natural end (the adapter reports it as state 'stopped'
  // with source 'player-stop'; a manual stop uses 'stop'). On end — while cruise is engaged —
  // the SAME stage loads the next drawer entry and plays it once booted.
  const sequentialUnsubscribes = new Map();
  // Cruise mode (Traktor-style): the explicit "software drives the deck" switch, offered in BOTH
  // modes. Auto-advance runs ONLY while engaged — normal playback never plays the next track by
  // itself. Queue mode arrives engaged (opening your queue IS the ask to keep listening);
  // collection mode arrives disengaged (you came to arrange decks).
  let cruiseEnabled = sequential;

  function toggleCruise() {
    cruiseEnabled = !cruiseEnabled;
    // Engaging cruise turns looping OFF on the live voices — auto-advance needs natural track
    // ends. Disengaging doesn't force loop back on: each track's own loop state is the user's
    // to drive from the transport.
    if (cruiseEnabled) {
      slotVoiceIds.forEach((id) => {
        if (!id) return;
        try {
          voiceRegistry.get(id)?.audioEngine?.setLoopEnabled?.(false);
        } catch (_) {}
      });
    }
    studio?.update?.({ cruise: { enabled: cruiseEnabled, onToggle: toggleCruise } });
  }

  // Drop-semantics toggle (the drawer toolbar): ON (default) = a track replaces the deck with its
  // FULL session, the world and orbiter it carries — loading a track means loading the track as its
  // author released it, so that is the default. OFF = a drop on an occupied stage swaps ONLY the
  // card's own dimension into the playing deck. Only a track has a session of its own, so an
  // orbiter and a world swap either way.
  let loadDefaultsEnabled = true;

  function toggleLoadDefaults() {
    loadDefaultsEnabled = !loadDefaultsEnabled;
    studio?.update?.({ loadDefaults: { enabled: loadDefaultsEnabled, onToggle: toggleLoadDefaults } });
  }
  // Stage index → true when the next voice added there should start playing (set by
  // an auto-advance; consumed by the add-pass in reconcileTo).
  const pendingAutoplayStages = new Set();

  function whenVoiceAudioEngine(voiceId, callback, tries = 40) {
    if (disposed) return;
    const engine = voiceRegistry.get(voiceId)?.audioEngine;
    if (engine) {
      callback(engine);
      return;
    }
    if (tries > 0) setTimeout(() => whenVoiceAudioEngine(voiceId, callback, tries - 1), 500);
  }

  // Fires `callback` once `voiceId` carries a NEW engine (a track/orbiter swap disposes the old one
  // and everything listening on it) whose resolved request matches `targetSession`. Requiring the
  // triad match — not just a fresh engine — makes rapid consecutive swaps safe: only the LAST swap's
  // poll can ever fire (earlier targets never become the resolved request), and a failed swap that
  // fell back to a stub session matches nothing.
  function whenVoiceEngineRebuilt(voiceId, prevEngine, targetSession, callback, tries = 40) {
    if (disposed) return;
    const voice = voiceRegistry.get(voiceId);
    if (!voice) return; // the voice left its stage — nothing to re-arm
    const engine = voice.audioEngine;
    const request = voice.dataManager?.activeConfigRequest;
    const matches =
      engine &&
      engine !== prevEngine &&
      request &&
      (request.trackId ?? null) === (targetSession.trackId ?? null) &&
      (request.orbiterId ?? null) === (targetSession.orbiterId ?? null) &&
      (request.entangledWorldId ?? null) === (targetSession.entangledWorldId ?? null);
    if (matches) {
      callback();
      return;
    }
    if (tries > 0) {
      setTimeout(() => whenVoiceEngineRebuilt(voiceId, prevEngine, targetSession, callback, tries - 1), 500);
    }
  }

  function advanceFromStage(index, endedVoiceId) {
    if (disposed || !cruiseEnabled || slotVoiceIds[index] !== endedVoiceId) return;
    const sourceId = slotSourceIds[index];
    const pos = sourceId ? drawerEntries.findIndex((e) => e.voiceId === sourceId) : -1;
    // Where to resume from — including the case where this card was REMOVED from the collection while
    // it was playing, and so is no longer in the rail at all (see `cruiseResumeIndex`).
    const follower = sourceId ? removedEntryFollowers.get(sourceId) : null;
    const from = cruiseResumeIndex(
      pos,
      follower ? drawerEntries.findIndex((e) => e.voiceId === follower) : -1,
      drawerEntries.length,
    );
    // The next BOOTABLE entry — a card that can't seed a stage (a world: no audio anywhere in the
    // platform) must not stall the cruise; skip past it.
    let next = null;
    for (let i = from; i < drawerEntries.length; i++) {
      if (isBootableEntry(drawerEntries[i])) {
        next = drawerEntries[i];
        break;
      }
    }
    if (!next) return; // end of the queue — stop (finite; the host owns "keep playing")
    pendingAutoplayStages.add(index);
    // The cruise taking a stage counts as much as a person taking it: a local card still waiting on
    // its coarse pass for this stage has to be retired here too, or it lands on top of the track the
    // cruise just chose — and, since the wait was a person's drop, takes the focus with it.
    localRail?.stageChanged(index, next.voiceId);
    // Auto-advance always means "the next deck", never an in-place swap — full replace even though
    // the stage is occupied by the just-ended voice.
    replaceStage(index, next);
  }

  function setupSequentialVoice(voiceId, index) {
    whenVoiceAudioEngine(voiceId, (engine) => {
      if (disposed || slotVoiceIds[index] !== voiceId) return;
      // Queue mode plays tracks once by nature; in collection mode looping only yields to an
      // ENGAGED cruise (a fresh deck otherwise keeps its track's own loop behavior).
      if (sequential || cruiseEnabled) {
        try {
          engine.setLoopEnabled?.(false);
        } catch (_) {}
      }
      const off = engine.addPlaybackStateListener?.((event) => {
        if (event?.state === 'stopped' && event?.source === 'player-stop') {
          advanceFromStage(index, voiceId);
        }
      });
      if (typeof off === 'function') sequentialUnsubscribes.set(voiceId, off);
      if (pendingAutoplayStages.delete(index)) {
        try {
          void engine.play?.();
        } catch (_) {}
      }
    });
  }

  async function reconcileTo() {
    if (disposed || !app || !studioHandle) return;
    const target = targetSubtype;

    const desired = buildDesiredArrangement(stageOverrides, target);
    const { removes, adds } = planReconcile(slotVoiceIds, desired);
    const occupiedBefore = slotVoiceIds.filter((id) => typeof id === 'string' && id.length > 0);
    const extendFullSelection =
      occupiedBefore.length > 1 &&
      occupiedBefore.every((id) => voiceRegistry.isSelected(id));

    for (const { index, voiceId } of removes) {
      app.removeVoice(voiceId);
      slotConfigUnsubscribes.get(voiceId)?.();
      slotConfigUnsubscribes.delete(voiceId);
      sequentialUnsubscribes.get(voiceId)?.();
      sequentialUnsubscribes.delete(voiceId);
      stageSwapTargets.delete(index);
      stagesAwaitingBoot.delete(index);
      slotVoiceIds[index] = null;
      slotSourceIds[index] = null;
    }
    if (slotVoiceIds.length > target) slotVoiceIds.length = target;
    if (slotSourceIds.length > target) slotSourceIds.length = target;

    let deferred = false;
    if (adds.length > 0) bootTrace('realm', 'reconcile add-pass', { adds: adds.length, removes: removes.length });
    for (const { index, entry } of adds) {
      if (!studioHandle.acquireCell(index)) {
        deferred = true; // stage cell not committed yet (a grow's new stage) — retry after the next frame
        continue;
      }
      // Only a track can seed a stage, so a placement always arrives with its own spine — there is
      // nothing left to resolve before the voice boots (a track placement never had a pre-verify).
      // Publish the position BEFORE the voice starts (the hook fires pre-start): this stage's
      // controls register and hydrate inside `start()`, and a collection-owned binding must be
      // able to resolve its stage right then. Assigning only after the await would race them.
      const voiceId = await bootTraceStep(`deck:${index}`, 'addVoice', () =>
        app.addVoice(entry, index, (id) => {
          slotVoiceIds[index] = id;
        }));
      if (disposed) return;
      slotVoiceIds[index] = voiceId ?? null;
      slotSourceIds[index] = voiceId ? entry.sourceVoiceId ?? entry.voiceId : null;
      // This stage has had its turn. A voice that failed to build gets no second one from here, so
      // the wait ends either way — a deferred stage (its cell not committed) never reached this
      // line and stays waiting for the retry below.
      stagesAwaitingBoot.delete(index);
      // The auto-advance end-listener arms in BOTH modes (a cheap subscription) — cruise itself
      // gates whether a natural end actually advances the stage.
      if (voiceId) setupSequentialVoice(voiceId, index);
      // Re-push studio state once this voice's own config resolves, so its slot's
      // focus-MIDI persistence id (still null right now) picks up the real orbiterId. Each voice
      // dispatches on its OWN per-voice `eventBus` (not `window` — see the per-voice event bus gap
      // noted in MIDIController), so this listens on that voice's own channel.
      const voiceEventBus = voiceId ? voiceRegistry.get(voiceId)?.eventBus : null;
      if (voiceId && voiceEventBus) {
        const onConfigResolved = () => {
          if (!disposed) pushStudioState();
        };
        voiceEventBus.addEventListener('dataManager:configUpdated', onConfigResolved);
        slotConfigUnsubscribes.set(voiceId, () =>
          voiceEventBus.removeEventListener('dataManager:configUpdated', onConfigResolved),
        );
      }
      if (extendFullSelection && voiceId && !voiceRegistry.isSelected(voiceId)) {
        try {
          // addToSelection (NOT toggleSelection) so growing the layout extends the selection onto the new
          // stage WITHOUT repointing the primary — the user's focused tile stays put through a grow.
          voiceRegistry.addToSelection(voiceId);
        } catch {
          // Voice may have been removed by a later reconcile while this add was resolving.
        }
      }
    }
    // Removing the active voice re-points the realm's active to a surviving voice automatically
    // (VoiceRegistry.unregister), which flows to the layout via onActiveChange — so no manual re-focus
    // is needed here. A drop focuses its own stage (loadStage), after this reconcile lands its voice.
    pushStudioState();
    // A stranded add (its stage cell wasn't committed yet — the compound boot-window grow+drop race)
    // has no other trigger to retry, so schedule one more reconcile on the next frame. Idempotent, and
    // self-terminating once the cell commits (no skip → no reschedule).
    if (deferred && !disposed && typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => enqueueReconcile());
    }
  }

  // Arrange mutators — wired to the studio for EVERYONE who can view (viewing grants full session-local
  // interaction). Only persisting the arrangement is edit-gated (see `onPersist`). A drag onto a stage
  // mints a NEW instance, so the same entry can sound on two stages at once (never vacates the other).
  /** Replace stage `index` with a fresh instance of `entry` (full boot — new placement, new voice) and
   *  focus that stage. Focusing the DROPPED stage (rather than root's advance-to-next-free, which suited
   *  sequential NFC fill) fits the explicit drag-drop model here and keeps the realm's active voice ↔ the
   *  layout focus in agreement. A drop of the entry already on that stage is a no-op + focus. */
  function replaceStage(index, entry, { focus = true, persist = true, swap = null } = {}) {
    const current = stageOverrides.get(index) ?? null;
    const currentSource = current ? current.sourceVoiceId ?? current.voiceId : null;
    // Already this card AND already playing its own pairing: nothing to rebuild, just focus it.
    // A stage carrying a SWAP is not "already there" even when the card matches — this is the
    // "load full sessions" drop of the stage's own card, whose whole purpose is to put the card
    // back with the session it came with. Returning early would leave the swapped orbiter playing
    // while the record said the stage was reset, and the two would disagree from then on.
    if (currentSource === entry.voiceId && !stageSwaps.has(index)) {
      if (focus) focusStage(index);
      return;
    }
    // A fresh instance: distinct minted voiceId, same audio + display fields; remember the source entry's
    // id so a repeat drop onto this stage is recognised as a no-op. A full replace supersedes any
    // swap still in flight on this stage — including one still awaiting its pre-verify (the stage's
    // old voice stays live until the reconcile, so without bumping the attempt token that stale
    // swap would still dispatch and its track-swap bookkeeping would overwrite THIS placement).
    stageSwapTargets.delete(index);
    swapAttempts.set(index, (swapAttempts.get(index) ?? 0) + 1);
    // A full replace re-seats the stage on the new card's OWN pairing, so whatever was swapped onto
    // the previous occupant is gone with it. Deleted here rather than at each call site so no path
    // into this function can leave a swap describing a card that is no longer on the stage.
    stageSwaps.delete(index);
    // ...unless the caller is RESTORING a stage that was left playing something other than its
    // card's pairing (the local rail putting a file back on its stage, a room snapshot describing a
    // deck whose orbiter or world was changed). Declared rather than smuggled in on the entry,
    // because the app has to RECORD it too: a stage whose pairing is only carried on its entry
    // looks unswapped to the next swap, which would then write a record missing the dimension
    // still playing.
    //
    // A VERSION rides along when the caller names one. A saved arrangement stores ids only and
    // means "the latest", which is what `?? null` keeps it saying. A ROOM names the exact build,
    // because every client has to boot the same one — dropping it here would seed this stage from
    // whatever the current release happens to be and quietly put two players on different audio.
    const placed = swap
      ? {
        ...entry,
        ...(swap.orbiterId
          ? { orbiterId: swap.orbiterId, orbiterVersion: swap.orbiterVersion ?? null }
          : {}),
        ...(swap.entangledWorldId
          ? {
            entangledWorldId: swap.entangledWorldId,
            entangledWorldVersion: swap.entangledWorldVersion ?? null,
          }
          : {}),
      }
      : entry;
    if (swap) stageSwaps.set(index, { ...swap });
    stageOverrides.set(index, { ...placed, voiceId: mintVoiceId(placed), sourceVoiceId: entry.voiceId });
    stagesAwaitingBoot.add(index);
    // The card loads until the VOICE is up — not until the reconcile returns.
    //
    // The reconcile was the wrong clock. `addVoice` awaits `session.start()`, and start() is a quick
    // kick by contract (it must not block the reconcile on a full load), so the chain below settles
    // in a millisecond or two while the orbiter it just asked for takes seconds. The card therefore
    // flashed its loader for one frame and went quiet, and in a room — where a played card also
    // LEAVES the hand — the card simply vanished and nothing anywhere said a deck was loading.
    //
    // `voiceSettled` is the realm's ready-or-deadline promise for that one voice, the same fact the
    // room's readiness ack already reports on, so the card and the peers now agree about when a deck
    // is up. It always settles (the voice races its own load deadline), and answers null for a voice
    // that never built — which `finally` treats as done, because there is nothing left to wait for.
    beginCardLoad(entry.voiceId);
    // Focus the dropped stage once its voice is live (its minted id lands in slotVoiceIds after the
    // reconcile). Pre-boot the chain resolves immediately and focusStage no-ops on the empty slot.
    Promise.resolve(enqueueReconcile())
      .then(() => {
        // Focus stays on the RECONCILE, not the load: the stage exists now, and making someone wait
        // out a boot before their drop is focused would undo what the drop was for.
        //
        // `focus: false` is the RESTORE putting local files back where they were left. Nobody dropped
        // anything, and the arrangement being restored has its own remembered focus — taking it would
        // mean the studio came back focused on whichever local card happened to be restored last.
        if (!disposed && focus) focusStage(index);
        return app?.voiceSettled?.(slotVoiceIds[index]) ?? null;
      })
      .catch(() => null)
      .finally(() => endCardLoad(entry.voiceId));
    // A RESTORE is not an edit, and must not write the arrangement back (rule 3 of the three
    // records). It would be writing what it just read — and worse, writing it before the other
    // records have finished restoring. The grid lands here at boot but this machine's local files
    // land later, once their library has opened, so a write-back in between states that their
    // stages are empty. That is how simply opening a collection erased the arrangement nobody
    // touched.
    if (persist) maybePersist();
  }

  /** A card dropped on stage `index`. An OCCUPIED stage (its voice's session already resolved) takes an
   *  in-place swap of the card's own dimension — unless "load full sessions" is engaged and the card can
   *  seed a session of its own, in which case the drop replaces the whole deck with the card's original
   *  session. An empty (or still-booting) stage takes the boot path, which only bootable cards may seed. */
  function loadStage(index, entry, options) {
    if (!entry || index < 0) return;
    // A file on this machine: never into a room, and never before its waveform exists (the
    // controller loads it itself once the coarse pass lands). Any card taking the stage retires a
    // local card still waiting for it.
    if (localRail && !localRail.beforeLoadStage(index, entry)) return;
    // In a room dealing a hand, only a card from THIS section's hand may be dealt. The gate sits here
    // — on the local deal — and nowhere else: the entry list itself is never filtered, because remote
    // deck commands and the late-join deck snapshot both resolve their cards through it.
    if (!cardIsInHand(entry)) return;
    // Room mode: every card deal is a synced, owner-gated structural event, published so every client
    // re-runs it locally. A TRACK card is always a full deck replace from the pool (no in-place
    // dimension swap); an ORBITER or WORLD card swaps its own dimension of the deck's live session.
    if (inRoom()) {
      if (entry.trackId) void roomLoadDeck(index, entry);
      else roomSwapDeck(index, entry);
      return;
    }
    const voiceId = slotVoiceIds[index];
    const current = voiceId ? voiceRegistry.get(voiceId)?.dataManager?.activeConfigRequest : null;
    const canBoot = isBootableEntry(entry);
    // The controller is told only on the paths that actually DO something to the stage, and never
    // before them. It releases the hold a remembered local file has on that stage, and a drop that
    // changes nothing must not: a card refused above (not in this hand) or below (not bootable, so
    // it cannot seed an empty stage) leaves the stage exactly as it was, while an early release
    // would let the next persist forget where that file lived — and relocating the file afterwards
    // would never bring it back to its stage.
    if (voiceId && current?.trackId && !(loadDefaultsEnabled && canBoot)) {
      localRail?.stageChanged(index, entry.voiceId);
      void swapStage(index, voiceId, entry, current);
      return;
    }
    if (!canBoot) return;
    localRail?.stageChanged(index, entry.voiceId);
    replaceStage(index, entry, options);
  }

  /**
   * A room-mode deck load: deal a pool card onto a deck. Local loads are gated to the deck this player
   * OWNS (the relay gates too; blocking locally means a non-owned load never even applies). A remote
   * load (server-stamped, already owner-validated) runs the SAME path on every client — incl.
   * spectators — with `remote:true` so it doesn't echo back out. Only a track can seed a deck. Warms a
   * PLAYABLE release before booting so the voice never boots from a cold, audio-less cache entry.
   */
  async function roomLoadDeck(index, entry, { remote = false, loadId = null, swap = null } = {}) {
    if (index < 0 || !isBootableEntry(entry)) return;
    if (!remote && !roomOwnsDeck(index)) return; // loading a deck you don't own is blocked
    bootTrace(`deck:${index}`, 'load requested', { trackId: entry.trackId, remote, keyed: Boolean(loadId) });
    // A RESYNC, not a command. Peer `player:state` snapshots carry the deck's contents so a late
    // joiner materializes it, and they arrive with no loadId — but they also arrive repeatedly, and
    // for decks that are ALREADY loaded, and this path used to re-run the whole load for each one.
    //
    // The cost is the release warm below. `ensurePlayableTrackRelease` force-refetches PAST the
    // cache whenever the release it gets carries no audio, so for such a track an already-settled
    // deck went back to the network on every snapshot it received — a `hydrate:2` fetch of several
    // seconds, repeating for as long as the room kept publishing state, on every client at once.
    // That is the "both players load the same card again and again, and it starts over each time it
    // finishes" loop: the deck never changed, but the app never stopped re-fetching it.
    //
    // So a resync only has work to do when the deck does NOT already agree with it. It stays fully
    // able to materialize an empty or different deck — that is what it is for — and it keeps its
    // hands off a deck that a keyed load command currently owns.
    if (!loadId) {
      const held = stageOverrides.get(index) ?? null;
      const heldSource = held ? held.sourceVoiceId ?? held.voiceId : null;
      if (heldSource === entry.voiceId) {
        // This deck is already this card — but a DEAL still resets the deck to the track's own
        // session, so the swap record has to be reset with it, exactly as the full path below does.
        // Skipping this would leave a card swap that the owner has since dealt away still recorded
        // here — and published on to late joiners.
        stageSwaps.delete(index);
        // Then the snapshot's own swaps go back on top, which is what converges a deck whose
        // orbiter or world was changed by somebody else. Only this path lays them on afterwards:
        // the deck is already built, so there is nothing to boot them into.
        applySnapshotSwaps(index, swap);
        return;
      }
      // Something is already loading on this deck RIGHT NOW, and the snapshot has to be read
      // against WHAT. This is only safe because a claim is released the moment its load settles: a
      // claim that outlived its load would make this an "ever dealt here?" test, and a client that
      // MISSED a later deal (a dropped relay message, a blink of a disconnect) could then never be
      // converged by a snapshot again — it would keep playing the previous card until the deck was
      // cleared, which is the exact divergence this path prevents.
      const inFlight = roomDeckLoadIds.get(index) ?? null;
      if (inFlight) {
        // This very card is already on its way onto this deck. The guard above cannot see it yet —
        // it reads the PLACEMENT, and the placement is only written once the release warm below
        // returns, which is a network round trip away. Without this, every snapshot landing in that
        // window starts a whole load of its own (`ensurePlayableTrackRelease` force-refetches past
        // the cache), and each one that finishes republishes and draws the next: the same
        // "it starts over each time it finishes" loop the placement guard was written to end, in
        // the one window that guard is blind in. A REJOIN is that window on every owned deck at
        // once, which is why the loop shows up there and almost nowhere else.
        if (inFlight.sourceVoiceId === entry.voiceId) return;
        // A different card, and a keyed LOAD COMMAND owns the deck: the command is the authority on
        // what this deck becomes, so a snapshot must not race it. An unkeyed resync holds no such
        // authority — it is a snapshot too, and a newer one supersedes it.
        if (inFlight.loadId) return;
      }
    }
    // Phase 1 — the load command. A local owner mints the loadId (published to the room); a remote
    // apply carries the owner's loadId. Everyone loads locally; the deck stays arming (not playable)
    // until Phase 2 (all slot-holders ack). A resolve failure surfaces `load-failed` to the room.
    const id = remote
      ? loadId
      : publishDeckLoad(index, { trackId: entry.trackId, version: entry.trackVersion ?? null });
    // Published, so the card is played — everyone else is about to spend it, and this client must
    // too even if the release below fails to resolve here. Only AFTER the publish: the refusals
    // above (the deck already holds this card, a load is in flight on it) reach nobody, and a drop
    // nobody heard must not quietly eat a card off the rail. The remote side is not spent here at
    // all — the room client spends it when the COMMAND arrives, which a snapshot is not.
    if (!remote) consumeCard(entry);
    // The card starts loading HERE, the moment the deal is accepted — not when the boot finally
    // reaches `replaceStage`. A played card leaves the hand at this same line (`consumeCard` above,
    // or the room client's `playRemoteCard` for somebody else's deal), and everything between here
    // and the placement is a network wait: the release warm alone can be seconds. Beginning the load
    // later left exactly that long where the card was gone from the hand and no loader had started
    // anywhere — the deck looked like nothing was happening to it.
    //
    // Held for a REMOTE apply too. The card is spent on every client, so every client owes the same
    // explanation for where it went.
    beginCardLoad(entry.voiceId);
    let cardLoadHeld = true;
    /** Hand this card's loader over (to `replaceStage`'s own) or give it up — exactly once. */
    const releaseCardLoad = () => {
      if (!cardLoadHeld) return;
      cardLoadHeld = false;
      endCardLoad(entry.voiceId);
    };
    // Claim the deck at MINT time, not after the release resolves. Two deals can be in flight on
    // one deck at once, and the slower one can finish last — claiming later would let it steal the
    // deck back from the newer card and report on its behalf.
    //
    // A RESYNC claims too, though it has no wire id to claim WITH. It is the same race — its own
    // release warm is what the next snapshot arrives during — so the claim states the card rather
    // than the load, and the guard above reads it. `loadId` stays null on it, which is what tells
    // that guard this is a snapshot's claim and not a command's.
    const claim = { loadId: id ?? null, sourceVoiceId: entry.voiceId };
    roomDeckLoadIds.set(index, claim);
    const isCurrent = () => !disposed && roomDeckLoadIds.get(index) === claim;
    /**
     * This load is over — give the deck up, so `roomDeckLoadIds` means "a load is in flight RIGHT
     * NOW" rather than "a load happened here once". Only ever releases OUR OWN claim: a newer deal
     * that has already overwritten the entry owns the deck, and clearing it here would tell that
     * one it had been superseded by a load that finished before it started.
     */
    const releaseClaim = () => {
      if (roomDeckLoadIds.get(index) === claim) roomDeckLoadIds.delete(index);
    };
    try {
      await bootTraceStep(`deck:${index}`, 'release warm', () => ensurePlayableTrackRelease(entry.trackId));
    } catch (error) {
      console.warn('[collection] room deck load failed to resolve a playable release', error);
      releaseCardLoad(); // nothing will boot — the card must not sit loading for ever
      if (isCurrent()) {
        // Only the load still owning this deck may fail it: the room releases a deck's barrier by
        // index without checking which load the verdict came from.
        if (id) markDeckLoadFailed(index, id);
        // A resync has no barrier to fail — and still has to let the deck go. A claim that outlived
        // its load would leave this deck unconvergeable: every later snapshot of that card would
        // read it as already on its way, and the deck would sit empty for the rest of the session.
        releaseClaim();
      }
      return;
    }
    if (!isCurrent()) {
      // A newer deal owns this deck and carries its own loader; ours has nothing left to narrate.
      releaseCardLoad();
      return;
    }
    bootTrace(`deck:${index}`, 'placing', { trackId: entry.trackId, remote });
    // A full deal resets the deck to the track's own session — any earlier card swaps are gone.
    stageSwaps.delete(index);
    // A snapshot's swapped orbiter/world rides INTO the boot rather than being laid on after it.
    // Applied afterwards, they rebuilt a deck that had just finished building — visibly loading it
    // twice, the first time on the wrong pairing — and the rebuild threw away the very engine the
    // snapshot's playback had just been applied to, which is how a rejoin could leave a deck
    // silent until somebody pressed play again.
    replaceStage(index, entry, { swap });
    // Handed over: `replaceStage` has begun this card's load of its own and holds it until the voice
    // is up. Released only AFTER that call, never before — the count must never reach zero in
    // between, or the card would blink out of the hand and back into it.
    releaseCardLoad();
    // A resync's claim ends HERE: the placement it was standing in for is recorded now, so the
    // guard at the top recognises this card by itself again. A keyed load keeps its claim until it
    // acks — the barrier still has to know which load is reporting.
    if (!id) releaseClaim();
    // Phase 2 — ack readiness only once the deck's voice is up WITH SOUND. The reconcile settling
    // means the stage was rebuilt, not that its audio survived; a device that couldn't hold the
    // track would otherwise report ready and leave everyone else playing against a silent deck.
    // Reporting the failure instead lets the room release the deck now rather than waiting out
    // the load barrier, and keeps every participant on the same picture of who is playing.
    if (id) {
      // Waiting on the voice can outlast the deal itself — a player may drop another card on this
      // deck first, and only the load still owning the deck may report.
      Promise.resolve(reconcileChain)
        .then(() => {
          if (!isCurrent()) return null;
          // A shell that can't report per-voice readiness tells us nothing either way, so keep the
          // old ack rather than inventing a failure. Only a realm that CAN answer is held to it.
          if (typeof app?.voiceSettled !== 'function') return { ok: true };
          return app.voiceSettled(slotVoiceIds[index]) ?? { ok: false };
        })
        .then((result) => {
          if (!isCurrent()) return;
          if (result?.ok && result.audioReady !== false) {
            notifyDeckLoaded(index, id);
            releaseClaim();
            return;
          }
          markDeckLoadFailed(index, id);
          releaseClaim();
          notifications.showToast(getT()('notifications.roomDeckLoadFailed'), 'error', 8000);
        })
        .catch((error) => {
          if (!isCurrent()) return;
          console.warn('[collection] room deck readiness check failed', error);
          markDeckLoadFailed(index, id);
          releaseClaim();
        });
    }
  }

  // Stage index → the load currently owning that deck: `{ loadId, sourceVoiceId }`, where `loadId`
  // is the wire id of a load COMMAND and null for a snapshot RESYNC (which claims the deck the same
  // way, and for the same window, but carries no authority a command has). A deck can be re-dealt
  // while an earlier load is still waiting on its voice; this is what tells a late verdict that it
  // has been superseded and must stay quiet, and what tells a snapshot that the card it names is
  // already on its way here.
  const roomDeckLoadIds = new Map();

  // Stage indices whose placement is still waiting for a reconcile to build its voice. Written when
  // a stage is placed and cleared the moment a reconcile has TRIED that stage — whether the voice
  // came up or not. That "whether or not" is the point: it is what stops a deck whose boot failed
  // from counting as forever-loading, which would keep it out of every future snapshot and leave
  // the relay serving a card this client is not holding for the rest of the session.
  const stagesAwaitingBoot = new Set();

  // Stage index → what that stage plays INSTEAD of its card's own pairing ({ orbiterId,
  // orbiterVersion, entangledWorldId, entangledWorldVersion } — only the swapped fields present).
  //
  // A stage is a TRIAD: a track, an orbiter and a world. Dropping an orbiter or world card on an
  // OCCUPIED stage swaps only that dimension and keeps the other two, so the card alone stops being
  // enough to say what the stage plays. This is the difference, and it has TWO readers:
  //
  //   a ROOM   reads it into its deck snapshots, so a late joiner (or the holder republishing after
  //            a handover) materializes the deck as it actually plays, not just its dealt track.
  //   the SAVED ARRANGEMENT reads it into `currentLayout().swaps`, so a reload comes back to the
  //            same thing for the same reason. Without it a stage returns to its track plus the
  //            author's default pairing, and the orbiter somebody chose is silently gone.
  //
  // Kept in every mode, not just in a room — it was room-only while the room was the only reader.
  // Recorded in ONE place (`swapStage`, as each swap is realized), and reset by a full track deal
  // (`replaceStage`) and by a clear, so the two readers can never disagree about a stage.
  //
  // The versions are for the ROOM, which has to put every client on the same build. A saved
  // arrangement stores ids only and comes back on the latest, which is what the live swap itself
  // requests — see `currentLayout()`.
  const stageSwaps = new Map();

  /**
   * Remember what stage `index` plays that its card does not name.
   *
   * A TRACK swap needs its own reading. It changes the track and CARRIES the stage's current orbiter
   * and world across (`swapDescriptor`), so the stage now plays a pairing that belongs to the card
   * that was there before — which the new card does not name at all. Recording only the previously
   * swapped dimension is not enough: drop a track onto a stage and its own orbiter comes across too,
   * so a reload would put the new track back with ITS defaults and quietly change what is playing.
   *
   * So a track swap states the whole live pairing, minus whatever the new card names itself. That
   * keeps the record sparse in the ordinary case (a card whose own pairing is what plays records
   * nothing) and exact in every other.
   */
  function recordStageSwap(index, dim, entry, session = null) {
    if (dim === 'track') {
      if (!session) return;
      const rec = {};
      if (session.orbiterId && session.orbiterId !== (entry.orbiterId ?? null)) {
        rec.orbiterId = session.orbiterId;
        rec.orbiterVersion = null;
      }
      if (session.entangledWorldId && session.entangledWorldId !== (entry.entangledWorldId ?? null)) {
        rec.entangledWorldId = session.entangledWorldId;
        rec.entangledWorldVersion = null;
      }
      if (rec.orbiterId || rec.entangledWorldId) stageSwaps.set(index, rec);
      else stageSwaps.delete(index);
      return;
    }
    const rec = { ...(stageSwaps.get(index) ?? {}) };
    if (dim === 'orbiter') {
      rec.orbiterId = entry.orbiterId;
      rec.orbiterVersion = entry.orbiterVersion ?? null;
    } else {
      rec.entangledWorldId = entry.entangledWorldId;
      rec.entangledWorldVersion = entry.entangledWorldVersion ?? null;
    }
    stageSwaps.set(index, rec);
  }

  /**
   * A room-mode card SWAP: an orbiter or world card dealt onto an OCCUPIED deck swaps only its own
   * dimension of the deck's live session — a synced, owner-gated structural event every client (incl.
   * spectators) re-runs through the same in-place swap path. An ORBITER swap rebuilds the deck's audio
   * engine, so it rides the two-phase readiness barrier exactly like a track deal; a WORLD swap is
   * visuals only (the engine keeps playing), so it ships single-shot and applies best-effort. A remote
   * command can arrive while this client's copy of the deck is still booting its track deal — retry
   * briefly, then surface a failure instead of silently diverging.
   */
  function roomSwapDeck(index, entry, { remote = false, loadId = null, tries = 40 } = {}) {
    const kind = entry?.orbiterId ? 'orbiter' : entry?.entangledWorldId ? 'world' : null;
    if (!kind || index < 0 || disposed) return;
    if (!remote && !roomOwnsDeck(index)) return; // swapping a deck you don't own is blocked
    const voiceId = slotVoiceIds[index];
    const current = voiceId ? voiceRegistry.get(voiceId)?.dataManager?.activeConfigRequest : null;
    if (!voiceId || !current?.trackId) {
      // No resolved session to swap against. A LOCAL drop on an empty/booting deck simply refuses
      // (nothing published, nothing diverges); a REMOTE command may just be ahead of our copy of the
      // deck's track deal — poll for the session, then surface the failure to the room.
      if (remote && tries > 0) {
        setTimeout(() => roomSwapDeck(index, entry, { remote, loadId, tries: tries - 1 }), 500);
      } else if (remote && loadId) {
        markDeckLoadFailed(index, loadId);
      }
      return;
    }
    // Phase 1 — the swap command (the wire mints a barrier loadId for an orbiter; a world has none).
    const id = remote
      ? loadId
      : publishDeckLoad(index, kind === 'orbiter'
        ? { orbiterId: entry.orbiterId, version: entry.orbiterVersion ?? null }
        : { entangledWorldId: entry.entangledWorldId, version: entry.entangledWorldVersion ?? null });
    // Published, so it is played here too. Remote swaps are spent by the room client when the
    // COMMAND arrives — this same function also runs for a snapshot's layered swaps, which describe
    // a deck that may still be holding a card dealt two sections ago.
    if (!remote) consumeCard(entry);
    void swapStage(index, voiceId, entry, current, {
      onSettled: (outcome, { audioReady = true } = {}) => {
        if (disposed) return;
        if (outcome === 'realized' || outcome === 'noop') {
          // The swap itself is recorded by `swapStage`, for both readers at once — see `stageSwaps`.
          // It is recorded even when the audio failed: the swap DID apply to this client's session,
          // and a snapshot describes what the deck IS, not whether this device can hear it.
          // Dropping it would hide the card from late joiners over a local audio problem.
          // Phase 2 — the deck is ready only if the replacement actually came up WITH SOUND. The
          // session being in place says the stage was rebuilt, not that its audio survived; a
          // device that couldn't hold the new orbiter would otherwise tell everyone it is ready
          // and leave the room playing against a deck that makes no sound here.
          if (id && audioReady) notifyDeckLoaded(index, id);
          else if (id) {
            markDeckLoadFailed(index, id);
            // The ack is what normally refreshes the relay's stored snapshot, and a failure doesn't
            // ack — so the owner publishes here instead. Without this the swap is recorded locally
            // but a late joiner still materializes the card it replaced, which is exactly what
            // keeping the record above was meant to avoid.
            if (!remote) getRoomClient()?.publishOwnStates?.();
            notifications.showToast(getT()('notifications.roomDeckLoadFailed'), 'error', 8000);
          }
          // A WORLD swap has no readiness ack (no barrier), so nothing else refreshes the relay's
          // stored deck snapshot — the owner republishes here or a late joiner misses the world.
          else if (!remote) getRoomClient()?.publishOwnStates?.();
        } else if (outcome === 'error' && id) {
          markDeckLoadFailed(index, id); // this client can't realize the swap — release, don't hang
        }
        // 'superseded': a newer deal owns the deck (its own barrier applies); 'timeout': the room
        // barrier's own timeout already releases the deck with a warning.
      },
    });
  }

  /** Apply a remote deck command — or a late-join deck snapshot, which can carry the dealt track PLUS
   *  swapped orbiter/world (the deck boots holding all three). Resolves pool cards by id (the pool is
   *  identical for everyone) and runs the manual load/swap path without re-publishing (the room
   *  client arms any barrier + passes the owner's loadId, which we ack once loaded). */
  function applyRemoteDeckLoad(index, entryRef, loadId) {
    if (!entryRef || typeof entryRef !== 'object') return;
    const { trackId } = entryRef;
    if (trackId) {
      const entry = drawerEntries.find((e) => e.trackId === trackId);
      if (!entry) return;
      // Only a SNAPSHOT names a track and a swapped dimension together (a load command carries the
      // dealt track alone), and the deck load takes both at once — see `replaceStage`.
      void roomLoadDeck(index, entry, { remote: true, loadId, swap: swapFromRef(entryRef) });
      return;
    }
    applyRemoteDeckSwap(index, entryRef, loadId);
  }

  /** The swapped dimensions a deck snapshot names, or null when the deck plays its card's own
   *  pairing. Versions travel with them: the room runs one build of an entity on every client. */
  function swapFromRef(ref) {
    const { orbiterId, orbiterVersion, entangledWorldId, entangledWorldVersion } = ref ?? {};
    if (!orbiterId && !entangledWorldId) return null;
    return {
      ...(orbiterId ? { orbiterId, orbiterVersion: orbiterVersion ?? null } : {}),
      ...(entangledWorldId
        ? { entangledWorldId, entangledWorldVersion: entangledWorldVersion ?? null }
        : {}),
    };
  }

  /** Lay a snapshot's swapped dimensions onto a deck that is ALREADY holding its card — the one
   *  case that cannot boot them in, because there is nothing left to boot. Each runs the ordinary
   *  synced swap path, which no-ops when the dimension is already what the snapshot says. */
  function applySnapshotSwaps(index, swap) {
    if (!swap) return;
    if (swap.orbiterId) applyRemoteDeckSwap(index, { orbiterId: swap.orbiterId });
    if (swap.entangledWorldId) applyRemoteDeckSwap(index, { entangledWorldId: swap.entangledWorldId });
  }

  /** Resolve a swap command's pool card by its OWN dimension's id and run the synced swap path. A ref
   *  that names no card in our pool can never load — surface it rather than hang the barrier. */
  function applyRemoteDeckSwap(index, ref, loadId = null) {
    const entry = ref?.orbiterId
      ? drawerEntries.find((e) => !e.trackId && e.orbiterId === ref.orbiterId)
      : ref?.entangledWorldId
        ? drawerEntries.find((e) => !e.trackId && e.entangledWorldId === ref.entangledWorldId)
        : null;
    if (entry) roomSwapDeck(index, entry, { remote: true, loadId });
    else if (loadId) markDeckLoadFailed(index, loadId);
  }

  /** The current deck contents by stage index (entryRef or null) — for `player:state` snapshots so a
   *  late joiner materializes the loaded decks: the dealt track plus any card swaps layered on it. */
  function roomDeckContents() {
    return slotSourceIds.map((sourceId, index) => {
      const entry = sourceId ? entryBySourceVoiceId.get(sourceId) : null;
      if (!entry?.trackId) return null;
      // Carry the PINNED version the live load command published — a snapshot that drops it makes a
      // late joiner materialize whatever the current release is, so the beat-locked room would run
      // two different pieces of audio.
      return {
        trackId: entry.trackId,
        version: entry.trackVersion ?? null,
        ...(stageSwaps.get(index) ?? {}),
      };
    });
  }

  /** Is this deck's card still on its way onto the stage — a load in flight, or a placement whose
   *  voice has not been built yet? A `player:state` snapshot has no way to say "loading": it names a
   *  card, or it says nothing, and nothing MEANS empty. So a deck in this state must be left out of
   *  the snapshot entirely, or every peer reads it as an eject and empties the deck it is watching.
   *  That is a REJOIN on every owned deck at once — see the note in `roomDeckBridge`.
   *
   *  Both halves END. A load releases its claim when it settles or fails, and a placement stops
   *  waiting the moment a reconcile has tried it. Neither is "this deck holds a card", which never
   *  becomes false while the deck is full and would mean a deck that failed to boot was never
   *  spoken about again. */
  function roomDeckMaterializing(index) {
    return roomDeckLoadIds.has(index) || stagesAwaitingBoot.has(index);
  }

  // Stage index → the latest swap attempt's token. A swap's pre-verify awaits the network, so an
  // older drop's verify can resolve AFTER a newer drop on the same stage — the newest drop must win
  // the stage outright (the older one aborts instead of dispatching a triad built from a stale view
  // of the session, which would silently revert the newer swap's dimension).
  const swapAttempts = new Map();
  // Stage index → the last swap triad DISPATCHED to that stage's voice, alive until that load cycle
  // reports done (or fails). A swap's kept dimensions must build on what the stage is BECOMING, not
  // only on what its resolver has already applied: the resolution behind the previous swap can take
  // seconds (version-pinned release fetches), and a world dropped right after an orbiter drop would
  // otherwise read the pre-swap request and re-dispatch the OLD orbiter inside the new triad.
  const stageSwapTargets = new Map();

  /** Swap ONLY the dropped card's dimension (track / orbiter / world) of the live session on stage
   *  `index`, keeping the other two. A world swap reuses the audio engine, so playback genuinely
   *  continues; a track/orbiter swap rebuilds the engine and the stage loads STOPPED (deliberate —
   *  the user presses play, no auto-resume). `onSettled` (optional) fires EXACTLY once with how the
   *  swap ended — `realized` | `noop` | `error` | `superseded` | `timeout` — the seam the room's
   *  synced swaps ack through. */
  async function swapStage(index, voiceId, entry, current, { onSettled = null } = {}) {
    // Build on the stage's in-flight target when one exists (a previous swap dispatched but not yet
    // finished loading) — otherwise on the live resolved request.
    const swap = swapDescriptor(stageSwapTargets.get(index) ?? current, entry);
    if (!swap) {
      onSettled?.('error'); // no swappable dimension / no resolved session — nothing can apply
      return;
    }
    if (swap.noop) {
      // The exact triad is already playing (or on its way) — but this stage may have arrived there
      // by a route nothing recorded (a restore that booted it already swapped), so state it before
      // focusing. Recording what is already true costs nothing; leaving it unsaid means a reload
      // loses a pairing that is plainly on screen.
      recordStageSwap(index, swap.dim, entry);
      maybePersist();
      focusStage(index);
      onSettled?.('noop');
      return;
    }
    const attempt = (swapAttempts.get(index) ?? 0) + 1;
    swapAttempts.set(index, attempt);
    beginCardLoad(entry.voiceId);
    // Verify the incoming entity's release BEFORE dispatching (cache-first, usually warm). A dead
    // target (archived/private) must never reach the engine's session-error fallback — that would
    // tear the live session down into a stub. On failure the stage simply keeps playing what it has,
    // and the drop stays retryable. An orbiter is verified as itself and nothing more — a swap
    // keeps the stage's CURRENT track, and an orbiter never carries a track of its own.
    try {
      if (swap.dim === 'track') {
        await fetchTrackRelease(entry.trackId, { version: entry.trackVersion ?? null, hydrate: 2 });
      } else if (swap.dim === 'orbiter') {
        await fetchOrbiterRelease(entry.orbiterId, {
          version: entry.orbiterVersion ?? null,
          useFallback: false,
        });
      } else {
        await fetchEntangledWorldRelease(entry.entangledWorldId, {
          version: entry.entangledWorldVersion ?? null,
        });
      }
    } catch (error) {
      console.warn('[collection] swap target is not available — stage keeps its session', error);
      endCardLoad(entry.voiceId);
      onSettled?.('error');
      return;
    }
    // The verify awaited — the stage may have been cleared, replaced, superseded by a newer drop,
    // or the app disposed meanwhile.
    if (disposed || slotVoiceIds[index] !== voiceId || swapAttempts.get(index) !== attempt) {
      endCardLoad(entry.voiceId);
      onSettled?.('superseded');
      return;
    }
    const voice = voiceRegistry.get(voiceId);
    if (!voice?.eventBus) {
      endCardLoad(entry.voiceId);
      onSettled?.('error');
      return;
    }
    // Rebuild the triad against the freshest view of the stage — the in-flight target if a previous
    // swap is still loading (its resolution may not have applied yet), else the live resolved
    // request. Building on anything staler would silently revert another dimension's recent swap.
    const live = stageSwapTargets.get(index) ?? voice.dataManager?.activeConfigRequest ?? current;
    const fresh = swapDescriptor(live, entry);
    if (!fresh || fresh.noop) {
      endCardLoad(entry.voiceId);
      if (fresh?.noop) focusStage(index);
      onSettled?.(fresh?.noop ? 'noop' : 'error');
      return;
    }
    const prevEngine = voice.audioEngine ?? null;
    // The stage's pending target: what this dispatch is turning the session into. Later swaps on
    // this stage build on it; it dies when this load cycle settles (the resolved request is
    // authoritative again) or when a newer dispatch replaces it.
    const swapTarget = {
      trackId: fresh.session.trackId,
      orbiterId: fresh.session.orbiterId,
      entangledWorldId: fresh.session.entangledWorldId,
      trackVersion: fresh.requested.trackVersion,
      orbiterVersion: fresh.requested.orbiterVersion,
      entangledWorldVersion: fresh.requested.entangledWorldVersion,
    };
    stageSwapTargets.set(index, swapTarget);
    // The card's loader runs until THIS load cycle finishes: the voice reports done (or an error)
    // on its own bus at the end of every bootstrap, swaps included. The timeout is a backstop for
    // a voice removed mid-swap, whose bus never reports again.
    let cardLoadSettled = false;
    let cardLoadTimer = null;
    const settleCardLoad = (outcome, { audioReady = true } = {}) => {
      if (cardLoadSettled) return;
      cardLoadSettled = true;
      voice.eventBus.removeEventListener(LOAD_PROGRESS_EVENT, onSwapLoadProgress);
      voice.eventBus.removeEventListener(LOAD_ERROR_EVENT, onSwapLoadError);
      if (cardLoadTimer) clearTimeout(cardLoadTimer);
      // Only THIS swap's target may be retired — a newer dispatch owns the entry now.
      if (stageSwapTargets.get(index) === swapTarget) stageSwapTargets.delete(index);
      endCardLoad(entry.voiceId);
      onSettled?.(outcome, { audioReady });
    };
    const onSwapLoadError = () => settleCardLoad('error', { audioReady: false });
    const onSwapLoadProgress = (event) => {
      if (!event?.detail?.done) return;
      // Every bootstrap cycle on this voice reports done — including an EARLIER layered swap's.
      // Only settle when THIS swap's cycle finished (the live request carries the target triad),
      // or when a newer dispatch superseded this one (its own listener owns the rest). Settling on
      // any done would clear the pending target early and a follow-up drop would build from a
      // stale view of the stage.
      const liveRequest = voiceRegistry.get(voiceId)?.dataManager?.activeConfigRequest;
      const realized =
        liveRequest &&
        (liveRequest.trackId ?? null) === swapTarget.trackId &&
        (liveRequest.orbiterId ?? null) === swapTarget.orbiterId &&
        (liveRequest.entangledWorldId ?? null) === swapTarget.entangledWorldId;
      // The session being in place is not the same fact as the engine having come up with sound:
      // a bootstrap reports done either way, and only the cycle itself knows which happened. A
      // caller that has to tell peers whether this deck is audible needs the second fact too.
      if (realized) {
        // The ONE place a realized swap is remembered, for both of `stageSwaps`' readers — the
        // room's deck snapshot and the saved arrangement. A track swap is not recorded here: it
        // re-seats the placement itself, which happens at dispatch below.
        recordStageSwap(index, fresh.dim, entry, fresh.session);
        // ...and the arrangement is written. A track swap already persisted at dispatch, but it has
        // to persist AGAIN here: the pairing it carried across is only known to be playing once the
        // swap is realized, and the dispatch-time write went out without it.
        maybePersist();
        settleCardLoad('realized', { audioReady: event.detail.audioReady !== false });
      } else if (stageSwapTargets.get(index) !== swapTarget) settleCardLoad('superseded');
    };
    voice.eventBus.addEventListener(LOAD_PROGRESS_EVENT, onSwapLoadProgress);
    voice.eventBus.addEventListener(LOAD_ERROR_EVENT, onSwapLoadError);
    cardLoadTimer = setTimeout(() => settleCardLoad('timeout'), 45000);
    voice.eventBus.dispatchEvent(
      new CustomEvent('orbiters:session-load', {
        detail: {
          session: { ...fresh.session, requested: fresh.requested, source: 'studio-swap' },
          payload: { source: 'studio-swap' },
        },
      }),
    );
    if (fresh.dim === 'track') {
      // A track swap re-seats the stage's IDENTITY (the drawer underline + the persisted order follow
      // the new track). Mutate the placement in place keeping its minted voiceId so the next reconcile
      // sees zero delta — the live voice already carries the new session. Orbiter/world swaps are
      // session-local by design: the placement (and anything persisted) stays the original card.
      const existing = stageOverrides.get(index);
      if (existing) {
        stageOverrides.set(index, { ...entry, voiceId: existing.voiceId, sourceVoiceId: entry.voiceId });
      }
      // `stageSwaps` is deliberately NOT cleared here. A track SWAP changes only the track and
      // carries the stage's current orbiter and world across (`swapDescriptor`), so a pairing
      // swapped onto this stage is still playing — now over a different track. Clearing it would
      // save an arrangement that does not match what can be heard. A full REPLACE is the operation
      // that resets a stage to its card's own pairing, and it clears the record there.
      slotSourceIds[index] = entry.voiceId;
      pushStudioState();
      maybePersist();
    }
    focusStage(index);
    // A rebuild (track/orbiter swap) disposes the old engine — and the auto-advance end-listener
    // with it. Re-arm on the NEW engine once it carries the swapped session; no autoplay (the stage
    // loads stopped, and cruise advances only from a natural end). A world swap keeps the engine,
    // so its listener survives untouched.
    if (fresh.dim !== 'world') {
      whenVoiceEngineRebuilt(voiceId, prevEngine, fresh.session, () => {
        if (slotVoiceIds[index] !== voiceId) return;
        sequentialUnsubscribes.get(voiceId)?.();
        sequentialUnsubscribes.delete(voiceId);
        setupSequentialVoice(voiceId, index);
      });
    }
  }

  /** Clear stage `index` (the per-stage remove control). Also aborts any swap still in flight on
   *  the stage (attempt bump) — its voice stays live until the reconcile removes it. In a room the
   *  eject is a synced, owner-gated structural event: a local clear on an owned deck publishes, and
   *  every client (incl. spectators) empties that deck; a `remote` apply runs the same path without
   *  re-publishing. */
  function clearStage(index, { remote = false } = {}) {
    if (index < 0) return;
    localRail?.stageChanged(index);
    if (inRoom() && !remote) {
      if (!roomOwnsDeck(index)) return; // clearing a deck you don't own is blocked
      publishDeckClear(index);
    }
    stageSwaps.delete(index); // a cleared stage plays nothing, so it has no swap to remember
    // An emptied deck has nothing left to report ready: retire whatever load was still waiting on
    // its voice so its verdict can't arrive after the card is gone.
    roomDeckLoadIds.delete(index);
    stageSwapTargets.delete(index);
    stagesAwaitingBoot.delete(index);
    swapAttempts.set(index, (swapAttempts.get(index) ?? 0) + 1);
    stageOverrides.set(index, null);
    enqueueReconcile();
    maybePersist();
  }

  /** Reorder the drawer list (drag one card before another / to the end). Display + persist order only —
   *  it does not move loaded stages (matches root's drawer reorder). */
  function reorderEntries(sourceVoiceId, beforeVoiceId) {
    // While a hand is dealt the rail's order is the DEAL's, not the user's — reordering it would only
    // shuffle a view of six cards that the next section replaces anyway.
    if (handSourceIds) return;
    const next = moveEntryBefore(drawerEntries, sourceVoiceId, beforeVoiceId);
    if (next === drawerEntries) return;
    drawerEntries.length = 0;
    drawerEntries.push(...next);
    // Re-index WITHOUT clearing: the map is every entry this session has known (see its declaration),
    // and a reorder is the same set in another order — clearing would only drop removed-but-staged cards.
    drawerEntries.forEach((entry) => entryBySourceVoiceId.set(entry.voiceId, entry));
    entriesSnapshot = drawerEntries.slice(); // the order actually changed → new identity for the card rail
    pushStudioState();
    // The local cards' order lives on this machine, whoever may edit the collection — `maybePersist`
    // writes it ahead of its own edit gate.
    maybePersist();
  }

  /** What removing a card from the rail MEANS in this session — or null when it is not offered.
   *
   *  `'collection'` — the card leaves the collection document (the authed item write).
   *  `'queue'`      — the track leaves the user's own queue (the queue write-back). The queue is the
   *                   audio-only rail you reach from "open queue in orbiters"; it is not a collection,
   *                   but it is just as much yours to manage, and dropping a track you don't want to
   *                   hear is the whole reason to look at the list.
   *
   *  Never in a ROOM, whichever mode: the pool a room plays is the creator's, dealt to everyone, so a
   *  room is a place to play together and not a place to administer what is in it — one participant
   *  deleting out of it would change what the others are holding.
   */
  function removeEntryMode() {
    if (!COLLECTION_ITEM_REMOVE_ENABLED || roomId) return null;
    // Queue mode: only the REAL `?queue=me` session carries the write-back. An ad-hoc `?tracks=` list
    // has none, and must never overwrite the user's stored queue.
    if (sequential) {
      return typeof collectionData?.persistQueueOrder === 'function' ? 'queue' : null;
    }
    // A collection: `canEdit` is the same permission root gates its own "Remove from collection" on.
    // Everything else in the rail is session-local; this one writes, so viewing is not enough.
    return permissions.canEdit && collectionData?.collectionId ? 'collection' : null;
  }

  /** Can THIS card be removed? Static per entry: a collection card needs the item id the write targets;
   *  a queue card is keyed by its trackId, which every queue entry has. A card that can never be
   *  removed is offered no menu item at all, rather than one that could only ever fail. */
  function entryIsRemovable(entry) {
    const mode = removeEntryMode();
    if (mode === 'queue') return Boolean(entry?.trackId);
    if (mode === 'collection') return Boolean(entry?.itemId);
    return false;
  }

  // Cards with a remove request in flight — a second right-click → Remove on the same card would
  // otherwise send a second DELETE for an item the first one is already taking out (mycelium answers
  // the loser 404 ITEM_NOT_FOUND, which would surface as a failure for a removal that did work).
  const removingSourceIds = new Set();

  // Removed source voiceId → the voiceId of the card that FOLLOWED it on the rail (null when it was
  // last). Read only by the cruise, to know where to carry on when the card that just finished is no
  // longer in the list. An identity, not a position: a position would go stale the moment anything
  // else is removed or reordered, and would then end the cruise early or resume on the wrong track.
  const removedEntryFollowers = new Map();

  /** Take one card out of the rail (both removal modes share this). Keeps the entry in
   *  `entryBySourceVoiceId` on purpose — see that map's declaration — and records the position it held,
   *  which is what lets the cruise carry on when the removed card is the one still playing. */
  function dropEntryFromRail(sourceVoiceId) {
    const at = drawerEntries.findIndex((c) => c.voiceId === sourceVoiceId);
    const follower = at >= 0 ? drawerEntries[at + 1]?.voiceId ?? null : null;
    removedEntryFollowers.set(sourceVoiceId, follower);
    // Anything already pointing AT this card now points past it, so a chain of removals still leads to
    // a card that is really on the rail instead of dead-ending on one that has also gone.
    removedEntryFollowers.forEach((value, key) => {
      if (value === sourceVoiceId) removedEntryFollowers.set(key, follower);
    });
    const next = drawerEntries.filter((candidate) => candidate.voiceId !== sourceVoiceId);
    drawerEntries.length = 0;
    drawerEntries.push(...next);
    entriesSnapshot = drawerEntries.slice(); // the list changed → new identity for the card rail
    pushStudioState();
    // Removing the last bootable card leaves a rail that can no longer start a stage. That notice was
    // computed once at boot, so without this the stages would just go on silently refusing every drop.
    studio?.update?.({ noPlayableEntries: !drawerEntries.some(isBootableEntry) });
  }

  /**
   * Remove one card from the COLLECTION (the rail's context menu — right-click on desktop, long-press
   * on touch). The authed write goes first and the rail follows it: nothing disappears from the list
   * until the backend has actually dropped the item, so a failed removal leaves the card where it is
   * rather than lying about the collection's contents.
   *
   * A copy of this entry that is currently PLAYING on a stage keeps playing. Removing a card from a
   * collection is a change to the collection, not a command to stop the music — the same reading root
   * takes, and the same one the dealt-hand rail takes when a played card leaves it.
   */
  async function removeEntry(sourceVoiceId) {
    const mode = removeEntryMode();
    if (!mode) return;
    const entry = drawerEntries.find((candidate) => candidate.voiceId === sourceVoiceId);
    if (!entry || !entryIsRemovable(entry)) return;
    if (removingSourceIds.has(sourceVoiceId)) return;

    // Queue mode: the write is the queue order MINUS this track — the same debounced write-back a
    // reorder already uses, so the rail drops the card at once and the queue follows, exactly as
    // dragging a card does here today. There is no per-item endpoint to await.
    if (mode === 'queue') {
      // The track that followed this one, resolved BEFORE the rail changes: if the removed track was
      // the queue's stored `currentTrackId`, that is the honest place for another device to resume —
      // the head of the list would send it back to the start.
      const at = drawerEntries.findIndex((candidate) => candidate.voiceId === sourceVoiceId);
      const followerTrackId = at >= 0 ? drawerEntries[at + 1]?.trackId ?? null : null;
      dropEntryFromRail(sourceVoiceId);
      const trackIds = drawerEntries.map((candidate) => candidate.trackId).filter(Boolean);
      collectionData.persistQueueOrder(trackIds, {
        // Taking out the last track is a real intention, not the boot race the empty guard is for.
        allowEmpty: true,
        // One discrete act, and the user has already been told it is done — so it must not sit on the
        // reorder debounce, where closing the tab within a second and a half would lose it.
        immediate: true,
        preferredCurrentTrackId: followerTrackId,
      });
      notifications.showToast(
        getT()('notifications.queueItemRemoved', { title: entry.title || getT()('studio.menu.untitled') }),
        'success',
        4000,
      );
      return;
    }

    removingSourceIds.add(sourceVoiceId);

    try {
      await removeCollectionItem(collectionData.collectionId, entry.itemId);
    } catch (error) {
      // "It was already gone" is not a failure of this action — the item is not in the collection,
      // which is exactly what was asked for. It happens whenever the same collection is open twice
      // (another tab, or root's collection view). Refusing to drop the card would strand a ghost on
      // the rail that every retry answers 404 for, so this takes the success path.
      //
      // The CODE, never a bare 404 status: an undeployed route or a deleted collection answers 404 too,
      // and reading those as success would tell the user a removal happened when nothing did.
      const alreadyGone = error?.code === 'ITEM_NOT_FOUND';
      if (!alreadyGone) {
        console.warn('[collection] failed to remove item from collection', error);
        if (!disposed) notifications.showToast(getT()('notifications.collectionItemRemoveFailed'), 'error', 6000);
        return;
      }
    } finally {
      removingSourceIds.delete(sourceVoiceId);
    }
    if (disposed) return;

    dropEntryFromRail(sourceVoiceId);
    // The rail's list changed, so the persisted order must follow it — otherwise the saved order goes
    // on naming a card the collection no longer has.
    maybePersist();
    notifications.showToast(
      getT()('notifications.collectionItemRemoved', { title: entry.title || getT()('studio.menu.untitled') }),
      'success',
      4000,
    );
  }

  /** Leave the collection Studio (the persistent nav's "Go back"). Full-page history back — collection
   *  mode is entered by a redirect from the site, so back returns there.
   *
   *  In a room this also has to SAY so. It used to be a bare `history.back()`, which reaches
   *  the relay as nothing but a socket going quiet — indistinguishable from a tunnel, so the seat
   *  was held for the full grace window (60s once playing) and the people still in the room went on
   *  being told this player was there. Announcing the departure first frees the seat at once. */
  function handleBack() {
    // "Go back" is a full-page navigation, so a pending arrangement inside the debounce window would
    // leave with the document. Send it first, on the leaving path — this is the last moment it can
    // be sent at all.
    flushPersist(true);
    getRoomClient()?.leaveRoom();
    if (typeof window !== 'undefined') window.history.back();
  }

  // Persist the arrangement AUTOMATICALLY after an edit (matching root — no Save button). Editor-gated and
  // fire-and-forget; still a stub-ack until the real write endpoint lands. The persisted `order` is the
  // SOURCE (roster) id per stage, not the minted realm id — `entry::N` can't map back to a roster entry on
  // restore, but source ids round-trip (a repeat id = a duplicate stage).
  function maybePersist() {
    // Queue mode: the same persistence moments (reorder, load, clear) write the new
    // order back to the user's queue instead of a collection layout.
    if (sequential && typeof collectionData?.persistQueueOrder === 'function') {
      collectionData.persistQueueOrder(
        drawerEntries.map((entry) => entry.trackId).filter(Boolean),
      );
      return;
    }
    // The LOCAL half of the arrangement — the rail order and the stages this machine's own files
    // hold — first, and ahead of every gate below. Those gates guard the COLLECTION's document, and
    // a `local:` id never enters it: someone with no rights over a collection can still put their
    // own files on its stages, and this is where that is remembered.
    localRail?.persistArrangement();
    // A ROOM must never write the collection's layout. Its stages are DEALT — the deck count comes
    // from the grant, and the cards on them were dealt by whoever is playing, including people with
    // no rights over this collection at all. An editor who happens to join would otherwise save the
    // room's table as the collection's saved arrangement, and everyone else would open the
    // collection to a table they never set. `inRoom()` rather than the boot's `roomId` because the
    // room client tears down when the relay is lost for good, and a torn-down room is a solo
    // session again — but this stays gated on the URL too, so the window between boot and the
    // client existing cannot leak a write.
    if (roomId || inRoom()) return;
    // An INLINE session has no collection to file an arrangement under, in either record.
    if (!usesStoredArrangement) return;
    // NOT gated on `canEdit`: rule 1 of the three records is that everybody writes their
    // arrangement somewhere, and which record it lands in is `flushPersist`'s decision, not this
    // one. Gating here is what used to make a viewer's stages the only ones nobody remembered.
    //
    // Coalesced, like the queue's own order write. Arranging is a BURST — a card dropped on a
    // stage, another cleared, the rail reordered twice — and each of those is a persist moment. Only
    // the last one describes the arrangement the user actually left, so the earlier writes are
    // wasted round trips that can also land out of order. The arrangement is already live locally,
    // so nothing waits on this.
    if (persistTimer) clearTimeout(persistTimer);
    persistPending = true;
    persistTimer = setTimeout(() => flushPersist(), PERSIST_DEBOUNCE_MS);
  }

  /**
   * Send the current arrangement (the debounce elapsed, or the studio is going away).
   *
   * The arrangement is read HERE rather than captured when the write was scheduled. Two reasons, and
   * the first one was a real bug: `slotSourceIds` is filled by the reconcile, which is ASYNC, so a
   * layout snapshotted inside `maybePersist` describes the stages as they were BEFORE the drop that
   * triggered it — every save lagged one action behind, and the last one never landed at all. The
   * second is what a debounce is for: only the final state of a burst is worth writing.
   */
  function flushPersist(leaving = false) {
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = null;
    if (!persistPending) return;
    persistPending = false;
    if (!collectionData.collectionId) return;

    // ONE builder for both records (rule 1). `currentLayout()` is the single reading of the stages,
    // so whichever record this write lands in, it describes the same arrangement — and the local
    // file placements the local rail writes alongside it were built from the same `stageSourceIds()`.
    const layout = currentLayout();

    // An EDITOR's arrangement IS the collection's.
    if (collectionLayoutIsOurs()) {
      Promise.resolve(
        saveCollectionLayout(collectionData.collectionId, layout, { immediate: leaving }),
      ).catch((error) => console.warn('[collection] failed to persist layout', error));
      // Their own record is retired by that same write, SERVER-SIDE — saying what the collection is
      // and retiring the personal view of it are one act. A second request from here could
      // half-happen (deleting the good record when the save failed) and is the one that gets
      // dropped on the way out of the studio, which would leave a personal arrangement shadowing
      // the collection this person had just saved.
      //
      // This browser's COPY of it still has to go, and only this side can do that: the mirror is
      // what a boot with no network reads, and one still holding the retired arrangement would put
      // it straight back over the layout just saved.
      if (PERSONAL_ARRANGEMENT_ENABLED && personalLayout) {
        void forgetLocalCollectionArrangement(collectionData.collectionId).catch(() => {});
      }
      personalLayout = null;
      return;
    }

    // Everyone else keeps their own. Ungated by `canEdit` on purpose: this is the record that
    // exists so that a person who cannot change a collection can still change their own view of it.
    // Signed out, the write is refused server-side and nothing here waits on it — that session
    // keeps the collection's arrangement plus its own local files, exactly as it always did.
    if (!PERSONAL_ARRANGEMENT_ENABLED) return;
    personalLayout = layout;
    Promise.resolve(
      saveCollectionArrangement(collectionData.collectionId, layout, { immediate: leaving }),
    ).catch((error) => console.warn('[collection] failed to persist the personal arrangement', error));
  }

  /**
   * What occupies each visible stage, by the source entry's voiceId — the user's INTENT, recorded
   * synchronously the moment they drop or clear a card, and not `slotSourceIds`, which is the
   * realised state the async reconcile fills in later. Both persist paths read it, so the
   * collection's layout and this machine's local placements always describe the same stages — this
   * is the WRITING half of the two-halves rule at the top of the boot.
   */
  function stageSourceIds() {
    const ids = [];
    for (let i = 0; i < targetSubtype; i++) {
      const held = stageOverrides.get(i) ?? null;
      ids.push(held ? held.sourceVoiceId ?? held.voiceId : null);
    }
    return ids;
  }

  /**
   * The arrangement as it stands, in the shape the restore reads back.
   *
   * `order` is built from `stageOverrides` — the user's INTENT, recorded synchronously the moment
   * they drop or clear a card — and not from `slotSourceIds`, which is the realised state the
   * reconcile fills in later. Intent is both always current and exactly what the restore feeds back
   * into, so the two ends are symmetric.
   *
   * `subtype` is the visible stage COUNT, and it has to be stated: an arrangement is walked by
   * position, so four stages with only the first two filled would otherwise be indistinguishable
   * from a two-stage studio, and someone who left four stages open would come back to two.
   */
  function currentLayout() {
    // A local entry's id names a file on THIS machine; in the collection's layout it would be a card
    // that exists nowhere else. The stage is saved as empty, and where that file actually sits is
    // remembered on this machine instead (see `persistArrangement` on the local rail controller).
    const order = stageSourceIds().map((sourceId) => (sourceId && !isLocalEntryId(sourceId) ? sourceId : null));
    return {
      // How the studio is SIZED, and which stage is focused. The values live in the studio's own React
      // state; `effectiveLayout` is this side's copy of them — seeded from the arrangement this session
      // opened on and updated by `onGeometryChange` whenever a divider drag or a deliberate focus
      // settles. Read from THAT rather than from the collection's layout regardless, because the
      // arrangement being shown may be this person's own: writing back geometry the person is not
      // looking at is how one record starts describing another record's studio.
      //
      // Always emitted, even for a session that never touched a divider. This is the arrangement AS IT
      // STANDS, whole — what the write path narrows to the fields that actually moved is its own
      // business, and it can only tell what moved if it is handed all of them.
      splitY: effectiveLayout?.splitY,
      splitXPrimary: effectiveLayout?.splitXPrimary,
      splitXSecondary: effectiveLayout?.splitXSecondary,
      focusedIndex: effectiveLayout?.focusedIndex,
      // The card drawer along the bottom: whether it was showing and how tall it was dragged. Same
      // source and the same reason as the splits above — it is how this studio was left, and the
      // drawer holds the COLLECTION's own cards, so an editor saying how it reads best is a real
      // thing to say about the collection.
      //
      // The local-files panel is deliberately NOT here. It shows files only this machine has, so how
      // it was left is remembered with THEM (`localRailController`): in this record it was one
      // person's viewing preference stored in everyone's, and a viewer opening someone else's
      // collection was handed the editor's open panel over a list of files they do not have.
      drawerOpen: effectiveLayout?.drawerOpen,
      drawerHeight: effectiveLayout?.drawerHeight,
      subtype: targetSubtype,
      order,
      entryOrder: drawerEntries.map((entry) => entry.voiceId).filter((id) => !isLocalEntryId(id)),
      // The stages playing something other than their card's own pairing. Sparse, and only for the
      // stages this record actually names: a stage holding a local file is blanked out of `order`
      // above, so its swap belongs to the local record instead and would be a placement pointing at
      // nothing here. Ids only — the versions in `stageSwaps` are the room's, which needs every
      // client on one build; an arrangement comes back on the latest, as the live swap requests.
      swaps: order.reduce((acc, sourceId, index) => {
        const swap = sourceId ? stageSwaps.get(index) : null;
        if (!swap) return acc;
        const shaped = { index };
        if (swap.orbiterId) shaped.orbiterId = swap.orbiterId;
        if (swap.entangledWorldId) shaped.entangledWorldId = swap.entangledWorldId;
        if (shaped.orbiterId || shaped.entangledWorldId) acc.push(shaped);
        return acc;
      }, []),
    };
  }

  /**
   * Warm the release cache for the first few drawer entries in the background, so an early
   * drag-to-stage is an instant cache hit rather than paying mycelium's slow per-item hydration.
   * `fetchTrackRelease` is cache-first and writes into the SAME `Constants` release cache the drag
   * path (addVoice → makeVoiceSession) consults, so a warmed entry needs no refetch. Fire-and-forget
   * and bounded (cap + concurrency); abandoned on dispose — an in-flight fetch simply lands in the
   * shared cache and its result is ignored.
   */
  // Warm-up ids currently in flight, shared across CALLS: rapid re-deals (two quick dice rolls, a
  // boundary landing mid-batch) would otherwise re-queue ids whose fetches are still airborne — the
  // sync cache check cannot see them — and double-fetch the same releases on mobile.
  const warmInFlight = new Set();
  function warmEntries(entries) {
    // A track entry warms the spine it would boot; an orbiter entry warms its own release for the
    // swap it can be dropped into. A world's release stays cold, as before.
    const seen = new Set();
    const queue = entries.filter((entry) => {
      if (!entry || (!entry.trackId && !entry.orbiterId) || seen.has(entry.voiceId)) return false;
      if (warmInFlight.has(entry.voiceId)) return false;
      seen.add(entry.voiceId);
      return true;
    });
    let cursor = 0;
    const runNext = async () => {
      while (!disposed && cursor < queue.length) {
        const entry = queue[cursor++];
        warmInFlight.add(entry.voiceId);
        try {
          if (entry.trackId) await fetchTrackRelease(entry.trackId, { hydrate: 2 });
          else if (entry.orbiterId) await fetchOrbiterRelease(entry.orbiterId);
        } catch (_) {
          // a warm-up miss is non-fatal — the drag path re-fetches and the per-tile loader narrates it
        } finally {
          warmInFlight.delete(entry.voiceId);
        }
      }
    };
    for (let i = 0; i < PREFETCH_CONCURRENCY; i += 1) runNext();
  }

  function prefetchReleases() {
    // Warm in drawer order (the first PREFETCH_LIMIT entries that have a release worth warming).
    // A room dealing a hand warms ITS cards instead (see `refreshHand`): the hand is chosen by a
    // hash, so the first few cards of the rail would usually be the wrong ones.
    if (handSourceIds) return;
    // A RESTORED stage goes first. Those releases are not a guess about what the user
    // might drag — they are already being booted by the first reconcile, so warming them behind the
    // head of the rail would have the boot waiting on a fetch queued after cards nobody asked for.
    // `warmEntries` de-duplicates by voiceId, so a staged card near the top of the rail is not
    // fetched twice.
    const staged = [...stageOverrides.values()]
      .filter(Boolean)
      .map((placement) => entryBySourceVoiceId.get(placement.sourceVoiceId ?? placement.voiceId))
      .filter(Boolean);
    // Filter BEFORE the cap: a world entry warms nothing, and letting it occupy one of the
    // PREFETCH_LIMIT slots starves a warmable track further down the rail.
    const rail = drawerEntries.filter((entry) => entry.trackId || entry.orbiterId);
    // De-duplicated BEFORE the cap, for the same reason the filter above is: a staged card that also
    // sits near the head of the rail would otherwise occupy two of the PREFETCH_LIMIT slots and warm
    // one card, starving a warmable track further down.
    const seenWarm = new Set();
    const queue = [...staged, ...rail].filter((entry) => {
      if (seenWarm.has(entry.voiceId)) return false;
      seenWarm.add(entry.voiceId);
      return true;
    });
    warmEntries(queue.slice(0, PREFETCH_LIMIT));
  }

  /** The drawer entries behind a list of hand source voiceIds, in hand order. */
  function handEntries(sourceIds) {
    return sourceIds.map((voiceId) => entryBySourceVoiceId.get(voiceId)).filter(Boolean);
  }

  /**
   * The draw the ROOM is running: the relay's replay of what the creator stated at mint, and — only
   * before that answer has arrived, which for the creator is the whole of the mint itself — this
   * client's own read of the grant. Everyone in a joined room reads the same one.
   */
  function roomDraw() {
    const pinned = roomStore.state.pinned;
    return {
      config: resolveDrawConfig(roomStore.state.draw ?? collectionData?.roomDraw),
      pinned: pinned.length > 0 ? pinned : mintPinned,
    };
  }

  /**
   * Deal one section's hand: the drawer voiceIds it puts on the rail, plus a placeholder card for any
   * dealt entity the pool can't resolve.
   */
  function dealSection(draw, sectionIds, sectionIndex, drawIndex) {
    const { byKind } = drawHand({
      pinned: draw.pinned,
      composition: draw.config.composition,
      roomId,
      sectionIds,
      sectionIndex,
      drawIndex,
    });
    const sourceIds = [];
    const missing = [];
    for (const [kind, ids] of Object.entries(byKind)) {
      for (const id of ids) {
        const entry = entryByEntity.get(`${kind}:${id}`);
        if (entry) sourceIds.push(entry.voiceId);
        else missing.push(unavailableHandCard(kind, id));
      }
    }
    return { sourceIds, missing };
  }

  /**
   * Re-deal when what the hand is dealt FROM moves, and push the new hand to the rail. The draw is
   * pure arithmetic, so this is cheap enough to hang off the score's own tick; a tick that leaves
   * every input where it was does nothing at all.
   *
   * Three things move it, and only three: the score reaching a new section, the DICE (`drawIndex`,
   * folded once by the relay and carried in the score transport), and the pinned pool arriving from
   * the room. Note `handSectionIndex` rather than `sectionIndex` — while the score runs free the
   * clock keeps going and the section pointer keeps advancing, but the hand freezes at the section
   * the free toggle happened in, or a section boundary would re-deal out from under a player who
   * has just rolled.
   *
   * A new hand never ejects anything: a deck already loaded and sounding plays on into the next
   * section. The hand only says what may be dealt NEXT.
   */
  function refreshHand() {
    if (disposed) return;
    const sections = roomScore.sections();
    const state = roomScore.current();
    const draw = roomDraw();
    const active =
      Boolean(roomId) && draw.config.enabled && Array.isArray(sections) && sections.length > 0 && state?.handSectionIndex >= 0;
    const sectionIndex = active ? state.handSectionIndex : null;
    const drawIndex = active ? state.drawIndex : 0;
    // Every input the deal actually depends on. It is a guard against re-dealing an identical hand
    // on every score tick, so anything it LEAVES OUT is a change that silently fails to re-deal.
    //
    // The composition has to be in here: the room's authoritative draw can arrive AFTER a hand was
    // already dealt — a reconnect where `roomScore` still holds its runtime, or a `room:joined` for a
    // score that was already running — and a key without it early-returns, leaving the rail showing a
    // hand of the wrong size for the rest of the room.
    //
    // The pool is fingerprinted by every id rather than by length-and-first-id, which cannot tell two
    // same-length rosters apart.
    const key = active
      ? [
          sectionIndex,
          drawIndex,
          draw.config.enabled,
          ...HAND_KINDS.map((kind) => draw.config.composition[kind]),
          // kind AND id: the piles are keyed per kind, so a pool whose ids held while a kind
          // changed deals a different hand and must not fingerprint as unchanged.
          draw.pinned.map((entry) => `${entry.kind}:${entry.id}`).join(','),
        ].join('|')
      : null;
    if (key === handKey) return;
    // The section the LAST deal was for, read back off the outgoing key (its first component, by
    // construction) rather than kept as a second piece of state the key could drift from.
    const previousSection = handKey === null ? null : Number(handKey.split('|')[0]);
    handKey = key;
    // A fresh deal is a fresh hand: nothing in it has been played yet. Cleared here rather than at
    // each of the three things that move a deal, so no path can re-deal and leave last hand's spent
    // cards striking out cards that were only just dealt.
    handConsumedKeys = new Set();
    if (!active) {
      handSourceIds = null;
      handMissingEntries = [];
      pushStudioState();
      return;
    }
    const sectionIds = sections.map((section) => section.id);
    const hand = dealSection(draw, sectionIds, sectionIndex, drawIndex);
    handSourceIds = hand.sourceIds;
    handMissingEntries = hand.missing;
    // A peer's played set for exactly this deal, held from before the hand existed.
    const pending = pendingHandConsumed;
    pendingHandConsumed = null;
    if (pending) applyHandConsumed(pending);
    pushStudioState();
    // Warm THIS hand, and the NEXT section's only when the SECTION moved. The drawer-order warm-up
    // would miss a hash-chosen hand entirely, and a section boundary must not land the player on a
    // cold card — but a dice roll re-deals the same section, and the "next section" it would warm is
    // speculative twice over: free mode has no boundary coming, and the next roll replaces it.
    // Capped: the relay admits compositions far larger than the default hand, and an uncapped warm
    // would queue up to two full hands of multi-second hydrations through the same two workers the
    // player's own drag needs.
    const sectionMoved = sectionIndex !== previousSection;
    warmEntries(
      [
        ...handEntries(hand.sourceIds),
        ...(sectionMoved ? handEntries(dealSection(draw, sectionIds, sectionIndex + 1, drawIndex).sourceIds) : []),
      ].slice(0, PREFETCH_LIMIT * 2),
    );
  }

  /**
   * A card has been PLAYED — it leaves the hand.
   *
   * Called from the one place every deal passes through, local and remote alike, so a card played on
   * any machine is spent on all of them without a message of its own. It is deliberately marked when
   * the command is accepted rather than when this device's own load finishes: a load that fails here
   * has still been published to everyone else, and a card that is gone for one player and not the
   * other is the divergence the whole draw exists to prevent.
   *
   * Ejecting the deck does not bring it back. A hand is what you have left to play, and the next
   * section (or the dice) is what refills it.
   */
  function consumeCard(entry) {
    if (!handSourceIds) return; // no hand dealt — the whole album is on the rail, nothing is spent
    if (markConsumed(`${entryKind(entry)}:${entryEntityId(entry)}`)) pushStudioState();
  }

  /** A stamped load command from another client played a card: resolve its reference in our own pool
   *  (identical for everyone) and spend it. A reference we cannot resolve names no card of ours. */
  function playRemoteCard(entryRef) {
    const key = entryRef?.trackId
      ? `track:${entryRef.trackId}`
      : entryRef?.orbiterId
        ? `orbiter:${entryRef.orbiterId}`
        : entryRef?.entangledWorldId
          ? `entangled-world:${entryRef.entangledWorldId}`
          : null;
    const entry = key ? entryByEntity.get(key) : null;
    if (entry) consumeCard(entry);
  }

  /**
   * Record one entity key as played, if it is a card of the hand ON THE RAIL right now. Returns
   * whether that changed anything, so callers push the rail once rather than per key.
   *
   * The membership test is what keeps the set meaning "of this hand": a deck can legitimately still
   * hold a card dealt in an earlier section, and a late joiner materializes exactly such decks from
   * their snapshots. Marking those would strike cards off a hand that never offered them.
   */
  function markConsumed(key) {
    if (handConsumedKeys.has(key)) return false;
    const voiceId = entryByEntity.get(key)?.voiceId;
    if (!voiceId || !handSourceIds.includes(voiceId)) return false;
    handConsumedKeys.add(key);
    return true;
  }

  /** The played cards as rail voiceIds — what the rail drops from the hand it shows. */
  function consumedSourceIds() {
    if (handConsumedKeys.size === 0) return [];
    return [...handConsumedKeys].map((key) => entryByEntity.get(key)?.voiceId).filter(Boolean);
  }

  /** The played cards for a deck snapshot: the whole hand's set, tagged with the deal it belongs to. */
  function handConsumedSnapshot() {
    if (!handSourceIds || !handKey) return null;
    return { deal: handKey, consumed: [...handConsumedKeys] };
  }

  /**
   * Fold a peer's snapshot of the played cards into ours — how a late joiner, or a client repairing a
   * gap, learns about cards played before it was listening. A UNION of matching deals only: it can
   * only ever add, so snapshots may arrive in any order and repeat, and a snapshot describing a hand
   * we have already re-dealt past says nothing about the one on the rail now.
   *
   * One that arrives before this client has dealt at all is HELD rather than dropped: at a join the
   * room's state can reach us in either order, and the deal that is a moment away may be the very one
   * it describes.
   */
  function applyHandConsumed(snapshot) {
    if (typeof snapshot?.deal !== 'string') return;
    if (!handSourceIds || !handKey) {
      pendingHandConsumed = snapshot;
      return;
    }
    if (snapshot.deal !== handKey) return;
    const incoming = Array.isArray(snapshot.consumed) ? snapshot.consumed : [];
    let added = false;
    for (const key of incoming) {
      if (typeof key === 'string' && markConsumed(key)) added = true;
    }
    if (added) pushStudioState();
  }

  /**
   * May this card be dealt right now? Outside a hand, every card may. Inside one, only the hand's own
   * cards that have not been played yet. This gates the LOCAL deal only — a remote command applies
   * whatever the room says, and a deck can legitimately still hold a card from an earlier hand.
   */
  function cardIsInHand(entry) {
    if (!handSourceIds) return !entry?.unavailable;
    if (entry?.unavailable || !handSourceIds.includes(entry?.voiceId)) return false;
    return !handConsumedKeys.has(`${entryKind(entry)}:${entryEntityId(entry)}`);
  }

  /**
   * Arm the room for a `?collection=…&room=…` boot: publish the fixed deck count (the room shape) so the
   * sync boot treats this as a multiplayer realm even while every deck is empty, register the deck-load
   * bridge (how remote loads apply locally + how deck contents are read for snapshots), and bring up the
   * room client on this tab's one socket. Decks then stay EMPTY — a voice boots only when a player deals
   * a card onto the deck they own (loadStage → roomLoadDeck), and that load is synced across clients.
   */
  /**
   * How many people hold a seat, as the GRANT recorded it at mint. The creator's tab sends this to
   * the relay as `shape.players`, and the relay refuses a room without one — so this is the number
   * the room is actually built with. The link no longer carries it; `?players=` remains only as the
   * fallback for a link minted before that was true.
   */
  function roomSeatCount() {
    const fromGrant = collectionData?.roomShape?.players;
    return Number.isInteger(fromGrant) && fromGrant > 0 ? fromGrant : getRoomPlayersFromUrl();
  }

  function armRoom() {
    setRoomRoster({
      deckCount: targetSubtype,
      players: roomSeatCount(),
      voiceOrder: [],
      collection: { name: collectionName, image: collectionCoverImage },
      joinCode: roomJoinCode,
      score: collectionData?.roomScore ?? null,
      // What the creator's tab mints the room with. A joiner sends none of this — it reaches them
      // from the relay, in the join snapshot.
      draw: wireDraw(),
      pinned: mintPinned,
    });
    setDeckLoadApplier(applyRemoteDeckLoad);
    // A stamped clear (or a snapshot whose deck is empty) empties the deck locally, no republish. An
    // already-empty deck is a cheap no-op reconcile.
    setDeckClearApplier((index) => { if (!disposed) clearStage(index, { remote: true }); });
    setDeckContentsProvider(roomDeckContents);
    setDeckPendingProvider(roomDeckMaterializing);
    setHandConsumedHandlers({ read: handConsumedSnapshot, apply: applyHandConsumed, played: playRemoteCard });
    ensureRoomClientArmed();
    watchRelayShape();
    // Follow the score's section pointer — that, and nothing else, is what deals a new hand.
    unsubscribeScore = roomScore.subscribe(refreshHand);
    refreshHand();
  }

  /**
   * Follow the relay's account of the room's shape. We boot on the grant's numbers, which
   * is the best any single client can know before it is joined; `room:joined` then states the shape
   * the whole room is running, and that is the one everybody must agree on. Pushing it into the
   * studio drives its stage count, which reconciles the realm through the existing subtype path.
   */
  function watchRelayShape() {
    if (unsubscribeRoomShape) return;
    let applied = null;
    unsubscribeRoomShape = roomStore.subscribe(() => {
      if (disposed) return;
      const voices = roomStore.state.shape?.voices;
      if (!Number.isInteger(voices) || voices === applied) return;
      applied = voices;
      const deckCount = clampSubtype(voices);
      if (deckCount === targetSubtype) return;
      console.info(`[room] adopting the relay's shape: ${deckCount} decks (was ${targetSubtype})`);
      setRoomRoster({
        deckCount,
        players: roomStore.state.shape?.players ?? roomSeatCount(),
        voiceOrder: [],
        collection: { name: collectionName, image: collectionCoverImage },
        joinCode: roomJoinCode,
        score: collectionData?.roomScore ?? null,
        draw: wireDraw(),
        pinned: mintPinned,
      });
      // Record the new target first, so a studio that has not mounted yet reads the RELAY's count
      // rather than the one we booted with. Where it has mounted, the prop change moves its stage
      // count, and its `onSubtypeChange` runs the reconcile — this call is then a cheap no-op.
      enqueueReconcile(deckCount);
      studio?.update?.({ roomDeckCount: deckCount });
    });
  }

  async function start() {
    bootTrace('realm', 'collection start');
    // Rule 2 of the three records: the grid is resolved ONCE, here, before a single stage exists.
    //
    // This person's own arrangement is one request, and it has to be answered before the stages are
    // built — an arrangement applied after the studio has mounted would be a visible re-shuffle, and
    // worse, the reconcile would tear down voices it had just booted from the collection's layout.
    // It is deliberately NOT the local library's boot, which is the slow part (presence checks and
    // the vault sweep) and still happens later, in the background, as it always did.
    //
    // A read that cannot get through answers `null` and the collection's own arrangement applies —
    // which is exactly what this app did before the personal record existed, and what it does when
    // the flag is off or nobody is signed in. There is no failure mode here that shows the wrong
    // arrangement; the worst case is the shared one.
    if (PERSONAL_ARRANGEMENT_ENABLED && usesStoredArrangement) {
      // BOUNDED, because this is the one request the studio waits on. A read that never answers —
      // a dead connection that neither resolves nor rejects — would otherwise hold the boot open
      // for ever, which is far worse than the thing it is here to fix. Past the deadline the read
      // is abandoned and the collection's own arrangement applies, exactly as when the server is
      // unreachable, the flag is off, or nobody is signed in.
      const abort = new AbortController();
      const deadline = setTimeout(() => abort.abort(), PERSONAL_ARRANGEMENT_READ_TIMEOUT_MS);
      try {
        personalLayout = await readCollectionArrangement(collectionData.collectionId, {
          signal: abort.signal,
        });
      } catch {
        personalLayout = null;
      } finally {
        clearTimeout(deadline);
      }
      if (disposed) return;
    }
    bootTrace('realm', 'personal arrangement read');
    resolveArrangement();

    // Stamp each card with whether IT can be removed, once — the mode is fixed for the session (URL
    // room id, permissions, queue-vs-collection), and the rail then needs no knowledge of either mode
    // to decide whether to offer the action on a given card.
    drawerEntries.forEach((entry) => {
      entry.removable = entryIsRemovable(entry);
    });
    entriesSnapshot = drawerEntries.slice();

    // The keyboard singleton normally boots with the first VOICE (Interaction.js) — an empty
    // collection would otherwise have no M key at all, locking the user out of MIDI-learn for
    // the shell targets until some orbiter loads. Initialize it voice-less here; the first
    // voice boot just updates its dependencies (same singleton, one document listener).
    KeyboardController.initialize({});
    studio = mountMultiStageStudio({
      rosterLength: roster.length,
      // The subtype the studio opens at must be the one this app is reconciling to, not the one the
      // stored layout happens to name. A restored arrangement can occupy MORE stages than the
      // collection's declared subtype (see the floor applied in the restore above), and the two must
      // not disagree: the studio would render two stages while the realm tried to fill four, and
      // `acquireCell` would defer the extra ones for ever.
      savedLayout: effectiveLayout ? { ...effectiveLayout, subtype: targetSubtype } : effectiveLayout,
      onFocusStage: focusStage,
      // How the studio is SIZED, and which stage is focused. The studio owns those four fields and
      // calls this when they settle — a divider drag that ended, a stage the user deliberately went
      // to. They are part of the arrangement like everything else, so they go into the record this
      // session writes (`effectiveLayout` is exactly what `currentLayout()` reads them back out of)
      // and ride the SAME debounce as a card drop rather than opening a second write path.
      onGeometryChange: (geometry) => {
        effectiveLayout = { ...(effectiveLayout || {}), ...geometry };
        // Only a mode that HAS an arrangement record writes one. Sizing the stages of a queue would
        // otherwise put the queue's own order back to the server, and an inline session would rewrite
        // its local file placements — two writes with nothing new in them, triggered by a drag.
        if (!usesStoredArrangement) return;
        maybePersist();
      },
      onSubtypeChange: (next) => enqueueReconcile(next),
      onBack: handleBack,
      // The drawer's entries + the arrange handlers are given to EVERYONE who can view — viewing grants
      // full session-local interaction (open drawer, drag onto stages, rearrange, remove, reorder).
      entries: drawerEntries.slice(),
      // No hand until the room's score says which section is playing (see `refreshHand`) — the rail
      // opens on the whole collection and narrows to the dealt hand when the room joins.
      handSourceIds,
      handMissingEntries,
      handConsumedIds: consumedSourceIds(),
      stageEntries: resolveStageEntries(),
      focusMidiPersistenceIds: resolveFocusMidiPersistenceIds(),
      onLoadStage: loadStage,
      onClearStage: clearStage,
      cruise: { enabled: cruiseEnabled, onToggle: toggleCruise },
      // Only a track can start a stage. A collection with none would otherwise open onto stages that
      // silently refuse every card — the studio says so on arrival instead.
      noPlayableEntries: !roster.some(isBootableEntry),
      loadDefaults: { enabled: loadDefaultsEnabled, onToggle: toggleLoadDefaults },
      // Collection/Orbiter MIDI mapping scope. Only offered where it means something: a real
      // collection with the feature enabled.
      midiScope:
        COLLECTION_MIDI_SCOPE_ENABLED && collectionData.collectionId
          ? { enabled: collectionMidiEnabled, onToggle: toggleCollectionMidi }
          : null,
      onReorderEntries: reorderEntries,
      // Remove-from-collection is the ONE rail action that is not session-local — it writes to the
      // collection — so unlike the arrange handlers above it is offered only when this viewer may edit
      // it (see `canRemoveEntries`). Null hides the menu item entirely rather than showing it disabled:
      // a viewer who cannot edit has nothing to learn from a greyed-out delete.
      onRemoveEntry: removeEntryMode() ? removeEntry : null,
      // The rail says what the card is leaving — a collection or your own queue. Two whole phrases,
      // not a noun slotted into one sentence: Spanish needs its own wording for each.
      removeEntryLabelKey:
        removeEntryMode() === 'queue' ? 'studio.menu.removeFromQueue' : 'studio.menu.removeFromCollection',
      registerMidiTarget: (binding) => MIDIControllerInstance?.registerMidiLearnTarget(binding),
      unregisterMidiTarget: (id) => MIDIControllerInstance?.unregisterMidiLearnTarget(id),
      // Shell actions (slot focus/add, pager, drawer) persist under THIS collection's slice.
      shellMidiCollectionId: COLLECTION_SHELL_MIDI_ENABLED ? collectionData.collectionId || null : null,
      // The in-app "Start room" affordance: a collection session can become a shared room. Hidden for
      // queue / inline boots (no collection to mint against) and when this session is already a room.
      collectionId: collectionData.collectionId || null,
      // Stored alongside an offline download so the downloads list can name this collection with no
      // connection to ask — the one place a display field has to survive the network going away.
      collectionName: collectionData.name || null,
      // What the studio header names this session. A queue has no collection behind it but is still
      // somebody's own; an inline roster is neither, so it gets no header at all.
      headerTitle: arrangementMode === 'queue' ? getT()('studio.header.queue') : collectionData.name || null,
      roomActive: Boolean(roomId),
      // In a room the layout is the FIXED deck count (from `?decks=`), not the pool size — so the
      // studio renders exactly that many stages regardless of how many cards the collection holds.
      roomDeckCount: roomId ? targetSubtype : null,
      // The local audio rail, when this session offers one (see `localRail` above).
      localRail: localRail ? localRail.studioProps() : null,
      // Persist is automatic + editor-gated (see maybePersist) — no Save control, matching root.
    });
    // The library boots after the studio is up: its cards join the rail through `applyLocalEntries`.
    if (localRail) {
      localRail.start().catch((error) => console.warn('[local] the local library did not start', error));
    }

    // Wait for the initial stages to commit so the realm's `acquireCell` finds real DOM.
    bootTrace('realm', 'studio mounting');
    studioHandle = await studio.ready;
    bootTrace('realm', 'studio ready');
    if (disposed) return;

    // The collection UI (placeholders + drawer) is on screen — dismiss the boot loader. Single-orbiter
    // hides it when its voice boots, but collection mode STARTS EMPTY (no voice), so the collection app
    // owns the dismissal, otherwise the z-9999 loader covers the studio forever.
    hideLoadingScreen();

    // Boot an EMPTY realm — every stage starts as a placeholder (matching root's mixed-collection).
    // Nothing is auto-loaded from the collection; the roster only populates the drawer, and a voice is
    // added to the realm only when the user drags a card onto a stage (loadStage → reconcile → addVoice).
    app = createMultiOrbiterApp({
      roster: [],
      makeVoiceSession,
      createRenderHost: () =>
        createViewportCompositor({ cellSource: { acquireCell: studioHandle.acquireCell } }),
    });

    // Reflect the realm's focused voice back onto the layout (Roman highlight). Ignore a null active
    // (fired while unregistering the last/only voice) — `indexOf(null)` would otherwise match the first
    // empty slot and land the highlight on a voiceless stage.
    unsubscribeActive = voiceRegistry.onActiveChange((activeId) => {
      if (activeId == null) return;
      const idx = slotVoiceIds.indexOf(activeId);
      if (idx >= 0) studioHandle.setActiveIndex(idx);
    });

    // Mirror the SELECTION set too (not just the primary): the dock's orbiter map brackets every
    // selected position and can build the set itself, so it needs the whole set to render honestly.
    unsubscribeSelection = voiceRegistry.onSelectionChange((selectedIds) => {
      const indices = selectedIds
        .map((id) => slotVoiceIds.indexOf(id))
        .filter((idx) => idx >= 0);
      // Mid-reconcile (a shrink, before slotVoiceIds is rewritten) a real selection can map to nothing
      // here. Pushing [] would read as "no multi-selection" and the map would bracket the primary
      // alone — a wrong answer. Hold the last known set until the mapping means something again.
      if (indices.length === 0 && selectedIds.length > 0) return;
      studioHandle.setSelectedIndices(indices);
    });

    // A room boot arms the multiplayer room NOW — the decks stay empty (each player deals their own),
    // so this only publishes the room shape + wires the deck-load bridge + brings up the room client on
    // the tab's one socket. A plain collection / queue boot skips it.
    if (roomId) armRoom();

    // Seed the studio's arrange UI (all placeholders) and apply any drop/count change that landed during
    // the boot window (queued but couldn't run until the realm existed).
    pushStudioState();
    enqueueReconcile();

    bootTrace('realm', 'realm starting');
    await app.start();
    bootTrace('realm', 'realm started');

    // The studio is interactive and the (empty) realm is up — now warm the release cache in the
    // background so the first drags are instant. Fire-and-forget; abandoned on dispose.
    prefetchReleases();
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    // Before anything is torn down: an arrangement still inside the debounce window is the user's
    // most recent one, and leaving is not a reason to discard it.
    flushPersist(true);
    localRail?.dispose();
    if (typeof window !== 'undefined') window.removeEventListener('pagehide', onPageHide);
    // Forget the room seam this boot published (if any) — a later realm must not read a dead
    // collection's deck map — and drop the deck-load bridge registrations this app owns.
    clearRoomRoster();
    resetDeckBridge();
    unsubscribeActive?.();
    unsubscribeSelection?.();
    unsubscribeSelection = null;
    unsubscribeRoomShape?.();
    unsubscribeRoomShape = null;
    unsubscribeScore?.();
    unsubscribeScore = null;
    slotConfigUnsubscribes.forEach((off) => off());
    slotConfigUnsubscribes.clear();
    // Context first, resolver second. The other order makes the context switch resolve every
    // still-registered stage control to its ORBITER slice — one fetch per stage plus a full
    // re-hydrate sweep — for widgets that are about to be torn down anyway.
    MIDIControllerInstance?.setCollectionMappingContext({ collectionId: null, enabled: false });
    MIDIControllerInstance?.clearSlotResolver(slotResolver);
    app?.dispose();
    studio?.dispose();
  }

  return { start, dispose };
}
